import { state } from '../state.js';
import { CategoryTreeNode, ExplorerEntityTypeMeta, EntityDiffChange } from '../types.js';
import { escapeHtml, formatRelativeTime, formatFileSize } from '../utils/formatters.js';
import { showToast } from '../utils/toast.js';
import { executeBatchSyncJob, openBatchProgressModal } from './batch.controller.js';
import { selectStagingPackage } from './staging.controller.js';

export const EXPLORER_ENTITY_TYPES: ExplorerEntityTypeMeta[] = [
  { key: 'categories', label: 'Categories', icon: '🌲', endpoint: 'categories' },
  { key: 'families', label: 'Families', icon: '👔', endpoint: 'families' },
  { key: 'attributes', label: 'Attributes', icon: '🏷️', endpoint: 'attributes' },
  { key: 'attribute_groups', label: 'Attribute Groups', icon: '📑', endpoint: 'attribute-groups' },
  { key: 'product_models', label: 'Product Models', icon: '📐', endpoint: 'product-models' },
  { key: 'products', label: 'Products', icon: '👟', endpoint: 'products' },
  { key: 'asset_families', label: 'Asset Families', icon: '🖼️', endpoint: 'asset-families' },
  { key: 'media_files', label: 'Media Files', icon: '📎', endpoint: 'media-files' },
  { key: 'channels', label: 'Channels', icon: '📺', endpoint: 'channels' },
  { key: 'locales', label: 'Locales', icon: '🌐', endpoint: 'locales' },
  { key: 'currencies', label: 'Currencies', icon: '💱', endpoint: 'currencies' },
  { key: 'association_types', label: 'Association Types', icon: '🔗', endpoint: 'association-types' },
  { key: 'reference_entities', label: 'Reference Entities', icon: '📚', endpoint: 'reference-entities' },
  { key: 'reference_entity_records', label: 'Reference Records', icon: '📝', endpoint: 'reference-entity-records' },
  { key: 'measurement_families', label: 'Measurement Families', icon: '📏', endpoint: 'measurement-families' },
  { key: 'users', label: 'User Management', icon: '👤', endpoint: 'users' },
];

const EXPLORER_TYPE_MAP = new Map(EXPLORER_ENTITY_TYPES.map((t) => [t.key, t]));

export function getExplorerTypeMeta(key?: string): ExplorerEntityTypeMeta {
  return EXPLORER_TYPE_MAP.get(key || state.explorer.entityType) || EXPLORER_ENTITY_TYPES[0];
}

export function isProductLikeEntityType(type: string): boolean {
  return type === 'products' || type === 'product_models';
}

export function isGalleryEntityType(type: string): boolean {
  return type === 'media_files' || type === 'asset_families';
}

function getItemId(item: any): string {
  return item?.identifier || item?.code || item?.username || '';
}

const DEFAULT_PREFERRED_LOCALES = ['en_US'];

function getPreferredLocales(): string[] {
  // Future: derive from active channel configuration or user preference.
  return [...DEFAULT_PREFERRED_LOCALES];
}

function extractLocalizedString(
  values: any[] | undefined,
  preferredLocales: string[]
): string | null {
  if (!Array.isArray(values) || values.length === 0) return null;

  for (const locale of preferredLocales) {
    const match = values.find(
      (v: any) =>
        v?.locale === locale &&
        v?.data !== null &&
        v?.data !== undefined &&
        String(v.data).trim() !== ''
    );
    if (match) return String(match.data);
  }

  for (const value of values) {
    if (
      value?.data !== null &&
      value?.data !== undefined &&
      String(value.data).trim() !== ''
    ) {
      return String(value.data);
    }
  }

  return null;
}

export function resolveProductCommercialName(item: any, preferredLocales?: string[]): string {
  if (!item || typeof item !== 'object') return '';

  const locales = preferredLocales?.length ? preferredLocales : getPreferredLocales();
  const values = item.values || {};

  // 1. Common commercial-name attributes
  for (const attr of ['name', 'label']) {
    if (values[attr]) {
      const name = extractLocalizedString(values[attr], locales);
      if (name) return name;
    }
  }

  // 2. Family-specific label attribute when available on the payload
  const familyLabelAttr = item.family_attribute_as_label;
  if (familyLabelAttr && values[familyLabelAttr]) {
    const name = extractLocalizedString(values[familyLabelAttr], locales);
    if (name) return name;
  }

  // 3. Fallback to the first available localized string value
  for (const attrValues of Object.values(values)) {
    const name = extractLocalizedString(attrValues as any[], locales);
    if (name) return name;
  }

  // 4. Last resort: raw identifier/code
  return item.identifier || item.code || '';
}

function getItemLabel(item: any, entityType?: string): string {
  if (!item) return '';

  if (isProductLikeEntityType(entityType || state.explorer.entityType)) {
    const commercialName = resolveProductCommercialName(item);
    if (commercialName) return commercialName;
  }

  if (item.labels && typeof item.labels === 'object') {
    return item.labels.en_US || Object.values(item.labels)[0] || '';
  }
  if (item.label && typeof item.label === 'string') return item.label;

  if (entityType === 'users' || state.explorer.entityType === 'users') {
    const firstName = item.first_name || '';
    const lastName = item.last_name || '';
    const fullName = `${firstName} ${lastName}`.trim();
    if (fullName) return fullName;
  }

  return '';
}

function getItemKind(item: any, entityType: string): string {
  if (!item) return '';
  switch (entityType) {
    case 'attributes':
      return item.type || 'attribute';
    case 'products':
      return item.family || '';
    case 'product_models':
      return item.family || '';
    case 'categories':
      return item.parent || 'root';
    case 'channels':
      return (item.locales || []).slice(0, 2).join(', ') || '';
    case 'currencies':
    case 'locales':
      return item.enabled === false ? 'Disabled' : 'Enabled';
    case 'measurement_families':
      return item.standard_unit_code || '';
    case 'users':
      return (item.roles || []).slice(0, 2).join(', ') || '';
    case 'reference_entities':
      return item.image ? 'Has image' : 'No image';
    case 'reference_entity_records':
      return 'Reference record';
    default:
      return '';
  }
}

function getItemStatus(item: any, entityType: string): { label: string; className: string } {
  if (!item) return { label: 'Unknown', className: 'status-failed' };

  if (entityType === 'products' || entityType === 'product_models') {
    const enabled = item.enabled !== false;
    return enabled
      ? { label: 'Enabled', className: 'status-completed' }
      : { label: 'Disabled', className: 'status-failed' };
  }

  if (entityType === 'currencies' || entityType === 'locales') {
    return item.enabled
      ? { label: 'Enabled', className: 'status-completed' }
      : { label: 'Disabled', className: 'status-failed' };
  }

  if (entityType === 'users') {
    return item.enabled === false
      ? { label: 'Disabled', className: 'status-failed' }
      : { label: 'Enabled', className: 'status-completed' };
  }

  return { label: 'Active', className: 'status-completed' };
}

export function renderExplorerEntitySelector(): void {
  const select = document.getElementById('explorer-entity-type') as HTMLSelectElement | null;
  const badge = document.getElementById('explorer-entity-badge');
  const title = document.getElementById('explorer-panel-title');
  const subtitle = document.getElementById('explorer-panel-subtitle');
  if (!select) return;

  const currentKey = state.explorer.entityType;
  const meta = getExplorerTypeMeta(currentKey);

  select.innerHTML = EXPLORER_ENTITY_TYPES.map(
    (t) => `<option value="${escapeHtml(t.key)}">${escapeHtml(t.icon)} ${escapeHtml(t.label)}</option>`
  ).join('');
  select.value = currentKey;

  if (badge) {
    badge.textContent = `${meta.icon} ${meta.label}`;
  }
  if (title) {
    title.textContent = `${meta.icon} ${meta.label} Explorer`;
  }
  if (subtitle) {
    subtitle.textContent = `Browse, search and inspect source ${meta.label.toLowerCase()} records from the active Akeneo instance.`;
  }

  const enabledFilterGroup = document.getElementById('explorer-enabled-filter-group');
  if (enabledFilterGroup) {
    enabledFilterGroup.style.display = isProductLikeEntityType(currentKey) ? 'flex' : 'none';
  }

  renderExplorerCategoryTabs();
}

export function renderExplorerCategoryTabs(): void {
  let container = document.getElementById('explorer-entity-tabs');
  if (!container) {
    const select = document.getElementById('explorer-entity-type');
    if (select && select.parentElement) {
      container = document.createElement('div');
      container.id = 'explorer-entity-tabs';
      container.className = 'explorer-category-tabs';
      select.parentElement.insertBefore(container, select);
    } else {
      return;
    }
  }

  if (!container.querySelector('.explorer-tab-btn')) {
    const categories = [
      { key: 'reference_entities', label: 'Reference Entities', icon: '📐' },
      { key: 'reference_entity_records', label: 'Reference Records', icon: '📑' },
      { key: 'measurement_families', label: 'Measurement Families', icon: '📏' },
      { key: 'users', label: 'User Management', icon: '👤' },
    ];
    container.innerHTML = categories
      .map(
        (cat) =>
          `<button class="explorer-tab-btn ${cat.key === state.explorer.entityType ? 'active' : ''}" data-entity-type="${cat.key}">${cat.icon} ${cat.label}</button>`
      )
      .join('');
    attachExplorerCategoryTabs();
  }

  container.querySelectorAll<HTMLButtonElement>('.explorer-tab-btn').forEach((btn) => {
    const type = btn.getAttribute('data-entity-type');
    btn.classList.toggle('active', type === state.explorer.entityType);
  });
}

function attachExplorerCategoryTabs(): void {
  const container = document.getElementById('explorer-entity-tabs');
  if (!container || container.dataset.initialized) return;

  container.addEventListener('click', (e) => {
    const btn = (e.target as HTMLElement).closest('.explorer-tab-btn') as HTMLButtonElement | null;
    if (!btn) return;
    const type = btn.getAttribute('data-entity-type');
    if (type) setExplorerEntityType(type);
  });

  container.dataset.initialized = 'true';
}

export function setExplorerEntityType(entityType: string): void {
  if (!EXPLORER_TYPE_MAP.has(entityType)) return;
  state.explorer.entityType = entityType;
  state.explorer.page = 1;
  state.explorer.searchAfter = undefined;
  state.explorer.nextCursor = undefined;
  state.explorer.cursorHistory = [undefined];
  renderExplorerEntitySelector();
  fetchExplorerItems();
}

export function setExplorerSearch(search: string): void {
  state.explorer.search = search;
  state.explorer.page = 1;
  state.explorer.searchAfter = undefined;
  state.explorer.nextCursor = undefined;
  state.explorer.cursorHistory = [undefined];
  fetchExplorerItems();
}

export function setExplorerEnabledFilter(value: '' | 'true' | 'false'): void {
  state.explorer.enabledFilter = value;
  state.explorer.page = 1;
  state.explorer.searchAfter = undefined;
  state.explorer.nextCursor = undefined;
  state.explorer.cursorHistory = [undefined];
  fetchExplorerItems();
}

export function setExplorerPageSize(limit: number): void {
  state.explorer.limit = limit;
  state.explorer.page = 1;
  state.explorer.searchAfter = undefined;
  state.explorer.nextCursor = undefined;
  state.explorer.cursorHistory = [undefined];
  fetchExplorerItems();
}

export function setExplorerPage(page: number): void {
  if (page < 1) return;

  if (isProductLikeEntityType(state.explorer.entityType)) {
    if (!state.explorer.cursorHistory) {
      state.explorer.cursorHistory = [undefined];
    }

    if (page === 1) {
      state.explorer.page = 1;
      state.explorer.searchAfter = undefined;
    } else if (page > state.explorer.page) {
      if (state.explorer.nextCursor) {
        state.explorer.cursorHistory[page - 1] = state.explorer.nextCursor;
        state.explorer.searchAfter = state.explorer.nextCursor;
      } else if (state.explorer.cursorHistory[page - 1]) {
        state.explorer.searchAfter = state.explorer.cursorHistory[page - 1];
      }
      state.explorer.page = page;
    } else {
      state.explorer.page = page;
      state.explorer.searchAfter = state.explorer.cursorHistory[page - 1];
    }
  } else {
    state.explorer.page = page;
  }

  fetchExplorerItems();
}

export function resetExplorerFilters(): void {
  state.explorer.search = '';
  state.explorer.enabledFilter = '';
  state.explorer.page = 1;
  state.explorer.searchAfter = undefined;
  state.explorer.nextCursor = undefined;
  state.explorer.cursorHistory = [undefined];
  state.explorer.selectedItemIds.clear();
  const searchInput = document.getElementById('explorer-search-input') as HTMLInputElement | null;
  if (searchInput) searchInput.value = '';
  const enabledSelect = document.getElementById('explorer-enabled-filter') as HTMLSelectElement | null;
  if (enabledSelect) enabledSelect.value = '';
  updateExplorerSelectionBar();
  fetchExplorerItems();
}

export async function fetchExplorerItems(): Promise<void> {
  const tableBody = document.getElementById('explorer-table-body');
  const emptyState = document.getElementById('explorer-empty-state');
  const paginationFooter = document.getElementById('explorer-pagination-footer');
  const meta = getExplorerTypeMeta();

  state.explorer.loading = true;

  if (tableBody) {
    tableBody.innerHTML = `
      <tr>
        <td colspan="5" style="text-align: center; padding: 2rem; color: var(--text-secondary);">
          <span class="spinner-sm"></span> Loading ${escapeHtml(meta.label.toLowerCase())} from source...
        </td>
      </tr>
    `;
  }
  if (emptyState) emptyState.style.display = 'none';

  const params = new URLSearchParams();
  params.set('page', String(state.explorer.page));
  params.set('limit', String(state.explorer.limit));
  if (state.explorer.search.trim()) {
    params.set('search', state.explorer.search.trim());
  }
  if (isProductLikeEntityType(state.explorer.entityType)) {
    if (state.explorer.enabledFilter) {
      params.set('enabled', state.explorer.enabledFilter);
    }
    if (state.explorer.searchAfter) {
      params.set('search_after', state.explorer.searchAfter);
    }
    if (state.explorer.search.trim()) {
      params.set('locales', getPreferredLocales().join(','));
    }
  }

  try {
    const res = await fetch(`/api/v1/discovery/explorer/${encodeURIComponent(state.explorer.entityType)}?${params.toString()}`);
    if (!res.ok) {
      const errData = await res.json().catch(() => ({}));
      const err: any = new Error(errData.message || `HTTP ${res.status}`);
      err.status = res.status;
      err.data = errData;
      throw err;
    }
    const data = await res.json();

    state.explorer.items = Array.isArray(data.items) ? data.items : [];
    state.explorer.totalCount = typeof data.totalCount === 'number' ? data.totalCount : state.explorer.items.length;
    state.explorer.hasMore = Boolean(data.hasMore || data.nextCursor);
    state.explorer.nextCursor = data.nextCursor || undefined;

    renderExplorerTable();
    renderExplorerPagination();
  } catch (err: any) {
    state.explorer.items = [];
    state.explorer.totalCount = 0;
    state.explorer.hasMore = false;
    state.explorer.nextCursor = undefined;

    const is403 =
      err.status === 403 ||
      err.data?.code === 'akeneo_permission_denied' ||
      err.data?.error === 'akeneo_permission_denied' ||
      String(err.message).includes('403');

    if (tableBody) {
      if (is403) {
        tableBody.innerHTML = `
          <tr>
            <td colspan="6" style="padding: 1.5rem;">
              <div class="permission-warning-card alert alert-warning" style="background: rgba(245, 158, 11, 0.1); border: 1px solid var(--status-warning, #f59e0b); border-radius: 8px; padding: 1.25rem;">
                <div style="display: flex; gap: 0.75rem; align-items: flex-start;">
                  <span style="font-size: 1.5rem; line-height: 1;">⚠️</span>
                  <div>
                    <h4 style="margin: 0 0 0.5rem 0; color: var(--text-primary); font-weight: 600;">Akeneo 403 Forbidden: Missing API Permissions</h4>
                    <p style="margin: 0 0 0.5rem 0; color: var(--text-secondary); font-size: 0.9rem;">
                      The active Akeneo API user/role does not have permission to view <strong>${escapeHtml(meta.label)}</strong>.
                    </p>
                    <div style="font-size: 0.85rem; color: var(--text-primary); margin-bottom: 0.5rem;">
                      <strong>Required Akeneo Permissions:</strong>
                      <ul style="margin: 0.25rem 0 0 1.25rem; padding: 0; line-height: 1.5;">
                        <li><strong>Product View</strong> (for products and product models)</li>
                        <li><strong>Category Tree access (Read/Write)</strong></li>
                        <li><strong>Locale access</strong></li>
                        <li><strong>Event Platform permissions</strong></li>
                      </ul>
                    </div>
                    <p style="margin: 0; font-size: 0.8rem; color: var(--text-secondary);">
                      Please ensure the API user/role is granted Product View, Category Tree access (Read/Write), Locale access, and Event Platform permissions in Akeneo PIM under <em>System &gt; Roles / Users</em>.
                    </p>
                  </div>
                </div>
              </div>
            </td>
          </tr>
        `;
      } else {
        tableBody.innerHTML = `
          <tr>
            <td colspan="5" style="text-align: center; padding: 2rem; color: var(--status-danger);">
              Failed to load records: ${escapeHtml(err.message)}
            </td>
          </tr>
        `;
      }
    }
    renderExplorerPagination();
  } finally {
    state.explorer.loading = false;
  }
}

export function renderExplorerTable(): void {
  const tableBody = document.getElementById('explorer-table-body');
  const tableResponsive = document.getElementById('explorer-table-responsive');
  const galleryGrid = document.getElementById('explorer-gallery-grid');
  const emptyState = document.getElementById('explorer-empty-state');
  if (!tableBody) return;

  const items = state.explorer.items;
  const entityType = state.explorer.entityType;
  const meta = getExplorerTypeMeta(entityType);

  if (!items || items.length === 0) {
    tableBody.innerHTML = '';
    if (tableResponsive) tableResponsive.style.display = 'block';
    if (galleryGrid) galleryGrid.style.display = 'none';
    if (emptyState) emptyState.style.display = 'block';
    return;
  }
  if (emptyState) emptyState.style.display = 'none';

  if (isGalleryEntityType(entityType)) {
    if (tableResponsive) tableResponsive.style.display = 'none';
    if (galleryGrid) {
      galleryGrid.style.display = 'grid';
      renderExplorerGallery(galleryGrid);
    }
    tableBody.innerHTML = '';
    return;
  }

  if (tableResponsive) tableResponsive.style.display = 'block';
  if (galleryGrid) galleryGrid.style.display = 'none';

  const rowsHtml = items
    .map((item) => {
      const id = getItemId(item);
      const label = getItemLabel(item, entityType);
      const kind = getItemKind(item, entityType);
      const status = getItemStatus(item, entityType);
      const updated = item.updated ? formatRelativeTime(item.updated) : 'N/A';
      const isSelected = state.explorer.selectedItemIds.has(id);

      const isProductLike = isProductLikeEntityType(entityType);
      const displayTitle = isProductLike ? label || id : id;
      const displaySubtitle = isProductLike ? id : label;

      return `
        <tr data-id="${escapeHtml(id)}">
          <td style="text-align: center;">
            <input type="checkbox" class="form-checkbox explorer-item-cb" data-id="${escapeHtml(id)}" ${isSelected ? 'checked' : ''} />
          </td>
          <td>
            <div style="display: flex; flex-direction: column; gap: 0.15rem;">
              <div style="display: flex; align-items: center; gap: 0.5rem;">
                <span>${meta.icon}</span>
                <strong>${escapeHtml(displayTitle)}</strong>
              </div>
              ${displaySubtitle ? `<span class="mono text-secondary" style="font-size: 0.8rem;">${escapeHtml(displaySubtitle)}</span>` : ''}
            </div>
          </td>
          <td>${isProductLike ? '' : label ? escapeHtml(label) : '<span class="text-secondary">—</span>'}</td>
          <td>${kind ? `<span class="badge badge-neutral">${escapeHtml(kind)}</span>` : '<span class="text-secondary">—</span>'}</td>
          <td>
            <span class="status-badge-inline ${status.className}">${status.label}</span>
          </td>
          <td class="text-secondary" style="font-size: 0.8rem;">${escapeHtml(updated)}</td>
          <td style="text-align: right;">
            <div style="display: inline-flex; gap: 0.35rem;">
              <button type="button" class="btn btn-xs btn-outline btn-explorer-inspect" data-id="${escapeHtml(id)}" title="Inspect Entity">Inspect</button>
              <button type="button" class="btn btn-xs btn-outline btn-explorer-diff" data-id="${escapeHtml(id)}" title="Compare Source vs Target">Diff</button>
              <button type="button" class="btn btn-xs btn-primary btn-explorer-sync" data-id="${escapeHtml(id)}" title="Sync to Target">Sync</button>
              <button type="button" class="btn btn-xs btn-outline btn-explorer-stage" data-id="${escapeHtml(id)}" title="Add to Virtual Target">Stage</button>
              <button type="button" class="btn btn-xs btn-danger btn-explorer-delete" data-id="${escapeHtml(id)}" title="Delete Entity">Delete</button>
            </div>
          </td>
        </tr>
      `;
    })
    .join('');

  tableBody.innerHTML = rowsHtml;

  const selectAllCb = document.getElementById('explorer-select-all-cb') as HTMLInputElement | null;
  if (selectAllCb) {
    selectAllCb.checked = items.length > 0 && items.every((item) => state.explorer.selectedItemIds.has(getItemId(item)));
  }

  tableBody.querySelectorAll('.explorer-item-cb').forEach((cb) => {
    cb.addEventListener('change', () => {
      const id = cb.getAttribute('data-id');
      if (id) toggleExplorerItemSelection(id, (cb as HTMLInputElement).checked);
    });
  });

  tableBody.querySelectorAll('.btn-explorer-inspect').forEach((btn) => {
    btn.addEventListener('click', () => {
      const id = btn.getAttribute('data-id');
      if (id) inspectExplorerEntity(id);
    });
  });

  tableBody.querySelectorAll('.btn-explorer-diff').forEach((btn) => {
    btn.addEventListener('click', () => {
      const id = btn.getAttribute('data-id');
      if (id) compareExplorerEntity(id);
    });
  });

  tableBody.querySelectorAll('.btn-explorer-sync').forEach((btn) => {
    btn.addEventListener('click', () => {
      const id = btn.getAttribute('data-id');
      if (id) syncExplorerItem(id);
    });
  });

  tableBody.querySelectorAll('.btn-explorer-stage').forEach((btn) => {
    btn.addEventListener('click', () => {
      const id = btn.getAttribute('data-id');
      if (id) openAddToVirtualTargetModal([{ entity_type: state.explorer.entityType, entity_id: id }]);
    });
  });

  tableBody.querySelectorAll('.btn-explorer-delete').forEach((btn) => {
    btn.addEventListener('click', () => {
      const id = btn.getAttribute('data-id');
      if (id) deleteExplorerItem(id);
    });
  });

  updateExplorerSelectionBar();
}

function getMediaFileThumbnail(item: any): string {
  const mime = String(item?.mime_type || '').toLowerCase();
  const href = item?._links?.download?.href || '';
  if (mime.startsWith('image/') && href) {
    return `<img src="${escapeHtml(href)}" alt="${escapeHtml(item.original_filename || item.code)}" class="gallery-thumb-img" loading="lazy" onerror="this.style.display='none'; this.parentNode.querySelector('.gallery-thumb-placeholder').style.display='flex';" />`;
  }
  return '';
}

function renderExplorerGallery(container: HTMLElement): void {
  const items = state.explorer.items;
  const entityType = state.explorer.entityType;
  const meta = getExplorerTypeMeta(entityType);

  const cardsHtml = items
    .map((item) => {
      const id = getItemId(item);
      const label = getItemLabel(item, entityType);

      if (entityType === 'media_files') {
        const filename = item.original_filename || item.code;
        const mime = item.mime_type || '—';
        const size = typeof item.size === 'number' ? formatFileSize(item.size) : '—';
        const dimensions = item.dimensions
          ? `${item.dimensions.width}×${item.dimensions.height}`
          : '—';
        const thumb = getMediaFileThumbnail(item);

        return `
          <div class="gallery-card" data-id="${escapeHtml(id)}">
            <div class="gallery-thumb">
              ${thumb}
              <div class="gallery-thumb-placeholder" style="${thumb ? 'display:none;' : ''}">
                <span class="gallery-thumb-icon">📎</span>
                <span class="gallery-thumb-ext">${escapeHtml(item.extension || '')}</span>
              </div>
            </div>
            <div class="gallery-card-body">
              <div class="gallery-card-title mono" title="${escapeHtml(filename)}">${escapeHtml(filename)}</div>
              <div class="gallery-card-meta">
                <span class="badge badge-info">${escapeHtml(mime)}</span>
                <span class="badge badge-neutral">${escapeHtml(size)}</span>
                <span class="badge badge-neutral">${escapeHtml(dimensions)}</span>
              </div>
              <div class="gallery-card-actions">
                <button type="button" class="btn btn-xs btn-outline btn-gallery-inspect" data-id="${escapeHtml(id)}">Inspect</button>
                <button type="button" class="btn btn-xs btn-outline btn-gallery-diff" data-id="${escapeHtml(id)}">Diff</button>
                <button type="button" class="btn btn-xs btn-primary btn-gallery-sync" data-id="${escapeHtml(id)}">Sync</button>
                <button type="button" class="btn btn-xs btn-outline btn-gallery-stage" data-id="${escapeHtml(id)}" title="Add to Virtual Target">Stage</button>
                <button type="button" class="btn btn-xs btn-danger btn-gallery-delete" data-id="${escapeHtml(id)}">Delete</button>
              </div>
            </div>
          </div>
        `;
      }

      // asset_families gallery card
      const familyCode = item.code || id;
      const familyLabels = renderLocalizedLabels(item.labels);
      const mainMedia = item.attribute_as_main_media
        ? `<span class="badge badge-accent">${escapeHtml(item.attribute_as_main_media)}</span>`
        : '<span class="text-secondary">No main media attribute</span>';
      const attrCount = Array.isArray(item.attributes) ? item.attributes.length : '—';

      return `
        <div class="gallery-card" data-id="${escapeHtml(id)}">
          <div class="gallery-thumb">
            <div class="gallery-thumb-placeholder">
              <span class="gallery-thumb-icon">🖼️</span>
            </div>
          </div>
          <div class="gallery-card-body">
            <div class="gallery-card-title mono" title="${escapeHtml(familyCode)}">${escapeHtml(familyCode)}</div>
            <div class="gallery-card-labels">${familyLabels}</div>
            <div class="gallery-card-meta">
              ${mainMedia}
              <span class="badge badge-neutral">${attrCount} attributes</span>
            </div>
            <div class="gallery-card-actions">
              <button type="button" class="btn btn-xs btn-outline btn-gallery-inspect" data-id="${escapeHtml(id)}">Inspect</button>
              <button type="button" class="btn btn-xs btn-outline btn-gallery-diff" data-id="${escapeHtml(id)}">Diff</button>
              <button type="button" class="btn btn-xs btn-primary btn-gallery-sync" data-id="${escapeHtml(id)}">Sync</button>
              <button type="button" class="btn btn-xs btn-outline btn-gallery-stage" data-id="${escapeHtml(id)}" title="Add to Virtual Target">Stage</button>
              <button type="button" class="btn btn-xs btn-danger btn-gallery-delete" data-id="${escapeHtml(id)}">Delete</button>
            </div>
          </div>
        </div>
      `;
    })
    .join('');

  container.innerHTML = `
    <div class="explorer-gallery-header">
      <span class="gallery-count">${items.length} ${escapeHtml(meta.label.toLowerCase())}</span>
    </div>
    <div class="explorer-gallery-cards">
      ${cardsHtml}
    </div>
  `;

  container.querySelectorAll('.btn-gallery-inspect').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const id = (btn as HTMLElement).getAttribute('data-id');
      if (id) inspectExplorerEntity(id);
    });
  });

  container.querySelectorAll('.btn-gallery-diff').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const id = (btn as HTMLElement).getAttribute('data-id');
      if (id) compareExplorerEntity(id);
    });
  });

  container.querySelectorAll('.btn-gallery-sync').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const id = (btn as HTMLElement).getAttribute('data-id');
      if (id) syncExplorerItem(id);
    });
  });

  container.querySelectorAll('.btn-gallery-stage').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const id = (btn as HTMLElement).getAttribute('data-id');
      if (id) openAddToVirtualTargetModal([{ entity_type: state.explorer.entityType, entity_id: id }]);
    });
  });

  container.querySelectorAll('.btn-gallery-delete').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const id = (btn as HTMLElement).getAttribute('data-id');
      if (id) deleteExplorerItem(id);
    });
  });

  container.querySelectorAll('.gallery-card').forEach((card) => {
    card.addEventListener('click', () => {
      const id = (card as HTMLElement).getAttribute('data-id');
      if (id) inspectExplorerEntity(id);
    });
  });
}

export function renderExplorerPagination(): void {
  const infoEl = document.getElementById('explorer-pagination-info');
  const prevBtn = document.getElementById('explorer-btn-prev') as HTMLButtonElement | null;
  const nextBtn = document.getElementById('explorer-btn-next') as HTMLButtonElement | null;
  const pagesContainer = document.getElementById('explorer-page-numbers');

  const { page, limit, totalCount, entityType, hasMore, nextCursor, items } = state.explorer;
  const isCursor = isProductLikeEntityType(entityType);

  const totalPages = Math.max(1, Math.ceil(totalCount / limit));
  const start = totalCount === 0 && items.length === 0 ? 0 : (page - 1) * limit + 1;
  const end = totalCount > 0 ? Math.min(page * limit, totalCount) : start + Math.max(0, items.length - 1);

  if (infoEl) {
    if (totalCount > 0) {
      infoEl.textContent = `Showing ${start} - ${end} of ${totalCount} records`;
    } else if (items.length > 0) {
      infoEl.textContent = `Showing ${start} - ${end} records (Page ${page})`;
    } else {
      infoEl.textContent = 'Showing 0 - 0 of 0 records';
    }
  }

  if (prevBtn) prevBtn.disabled = page <= 1;
  if (nextBtn) {
    if (isCursor) {
      nextBtn.disabled = !hasMore && !nextCursor;
    } else {
      nextBtn.disabled = page >= totalPages || totalCount === 0;
    }
  }

  if (pagesContainer) {
    let pagesHtml = '';
    if (isCursor && (!totalCount || totalCount <= items.length)) {
      const historyLen = state.explorer.cursorHistory?.length || 1;
      const maxPage = Math.max(page, historyLen);
      for (let p = 1; p <= maxPage; p++) {
        pagesHtml += `<button type="button" class="pagination-page-btn ${p === page ? 'active' : ''}" data-page="${p}">${p}</button>`;
      }
    } else {
      const maxVisiblePages = 5;
      let startPage = Math.max(1, page - Math.floor(maxVisiblePages / 2));
      let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
      if (endPage - startPage < maxVisiblePages - 1) {
        startPage = Math.max(1, endPage - maxVisiblePages + 1);
      }

      for (let p = startPage; p <= endPage; p++) {
        pagesHtml += `<button type="button" class="pagination-page-btn ${p === page ? 'active' : ''}" data-page="${p}">${p}</button>`;
      }
    }

    pagesContainer.innerHTML = pagesHtml;

    pagesContainer.querySelectorAll('.pagination-page-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        const p = Number(btn.getAttribute('data-page'));
        if (p && p !== state.explorer.page) {
          setExplorerPage(p);
        }
      });
    });
  }
}

function getAttributeTypeBadgeClass(type?: string): string {
  if (!type) return 'badge-neutral';
  if (type.includes('select')) return 'badge-accent';
  if (type.includes('reference') || type.includes('asset')) return 'badge-warning';
  if (type.includes('metric') || type.includes('price') || type.includes('number')) return 'badge-success';
  if (type.includes('image') || type.includes('file') || type.includes('media')) return 'badge-info';
  if (type === 'pim_catalog_boolean') return 'badge-info';
  if (type === 'pim_catalog_date') return 'badge-info';
  return 'badge-neutral';
}

function renderLocalizedLabels(labels?: Record<string, string>): string {
  if (!labels || typeof labels !== 'object' || Object.keys(labels).length === 0) {
    return '<span class="text-secondary">No localized labels</span>';
  }
  return Object.entries(labels)
    .map(
      ([locale, label]) =>
        `<span class="inspector-label-pill"><span class="inspector-locale">${escapeHtml(locale)}</span> ${escapeHtml(String(label || ''))}</span>`
    )
    .join('');
}

function renderYesNo(value?: boolean | null): string {
  if (value === true) return '<span class="badge badge-success">Yes</span>';
  if (value === false) return '<span class="badge badge-neutral">No</span>';
  return '<span class="text-secondary">—</span>';
}

function renderAttributeInspector(payload: any, options: any[] = []): string {
  const type = payload.type || 'unknown';
  const typeBadgeClass = getAttributeTypeBadgeClass(type);
  const labels = renderLocalizedLabels(payload.labels);

  const flagRows = [
    { label: 'Localizable', value: payload.localizable },
    { label: 'Scopable (Channel specific)', value: payload.scopable },
    { label: 'Required', value: payload.required },
    { label: 'Unique', value: payload.unique },
    { label: 'Grid filter', value: payload.useable_as_grid_filter },
    { label: 'Decimals allowed', value: payload.decimals_allowed },
    { label: 'Negative allowed', value: payload.negative_allowed },
    { label: 'WYSIWYG enabled', value: payload.wysiwyg_enabled },
  ];

  const flagsHtml = flagRows
    .map(
      (row) => `
        <div class="inspector-row">
          <span class="inspector-label">${escapeHtml(row.label)}</span>
          <span class="inspector-value">${renderYesNo(row.value)}</span>
        </div>
      `
    )
    .join('');

  const validationFields = [
    { key: 'group', label: 'Group' },
    { key: 'validation_rule', label: 'Validation rule' },
    { key: 'validation_regexp', label: 'Validation regexp' },
    { key: 'metric_family', label: 'Metric family' },
    { key: 'default_measurement_unit', label: 'Default unit' },
    { key: 'sort_order', label: 'Sort order' },
    { key: 'date_min', label: 'Date min' },
    { key: 'date_max', label: 'Date max' },
    { key: 'number_min', label: 'Number min' },
    { key: 'number_max', label: 'Number max' },
    { key: 'max_file_size', label: 'Max file size' },
  ];

  const validationHtml = validationFields
    .map((field) => {
      const value = payload[field.key];
      if (value === undefined || value === null || value === '') return '';
      return `
        <div class="inspector-row">
          <span class="inspector-label">${escapeHtml(field.label)}</span>
          <span class="inspector-value mono">${escapeHtml(String(value))}</span>
        </div>
      `;
    })
    .filter(Boolean)
    .join('');

  const availableLocales = Array.isArray(payload.available_locales)
    ? payload.available_locales.map((locale: string) => `<span class="badge badge-neutral">${escapeHtml(locale)}</span>`).join(' ')
    : '<span class="text-secondary">All locales</span>';

  let optionsHtml = '';
  if (type.includes('select')) {
    if (options.length === 0) {
      optionsHtml = `
        <div class="inspector-section">
          <div class="inspector-section-title">🎛️ Options</div>
          <p class="text-secondary">No options defined for this attribute.</p>
        </div>
      `;
    } else {
      const optionsRows = options
        .map((option: any) => {
          const optionLabels = renderLocalizedLabels(option.labels);
          return `
            <div class="inspector-option-item">
              <div class="inspector-option-code mono">${escapeHtml(option.code)}</div>
              <div class="inspector-option-labels">${optionLabels}</div>
            </div>
          `;
        })
        .join('');
      optionsHtml = `
        <div class="inspector-section">
          <div class="inspector-section-title">🎛️ Options <span class="badge badge-accent">${options.length}</span></div>
          <div class="inspector-options-list">${optionsRows}</div>
        </div>
      `;
    }
  }

  return `
    <div class="entity-inspector-rich">
      <div class="inspector-section">
        <div class="inspector-section-title">🏷️ Attribute Definition</div>
        <div class="inspector-row">
          <span class="inspector-label">Code</span>
          <span class="inspector-value mono">${escapeHtml(payload.code)}</span>
        </div>
        <div class="inspector-row">
          <span class="inspector-label">Type</span>
          <span class="inspector-value"><span class="badge ${typeBadgeClass}">${escapeHtml(type)}</span></span>
        </div>
        <div class="inspector-row">
          <span class="inspector-label">Labels</span>
          <span class="inspector-value">${labels}</span>
        </div>
        <div class="inspector-row">
          <span class="inspector-label">Available locales</span>
          <span class="inspector-value">${availableLocales}</span>
        </div>
      </div>

      <div class="inspector-section">
        <div class="inspector-section-title">⚙️ Flags & Validation</div>
        <div class="inspector-grid">${flagsHtml}</div>
        ${validationHtml ? `<div class="inspector-grid" style="margin-top: 0.75rem;">${validationHtml}</div>` : ''}
      </div>

      ${optionsHtml}
    </div>
  `;
}

function renderFamilyInspector(payload: any, variants: any[] = []): string {
  const labels = renderLocalizedLabels(payload.labels);
  const attributes = Array.isArray(payload.attribute_codes) ? payload.attribute_codes : [];

  const attributesHtml = attributes.length
    ? attributes
        .map(
          (code: string) => `
            <span class="badge badge-neutral inspector-attr-code">${escapeHtml(code)}</span>
          `
        )
        .join(' ')
    : '<span class="text-secondary">No attributes</span>';

  const requirements = payload.attribute_requirements || {};
  const requirementChannels = Object.keys(requirements);

  let requirementsHtml = '';
  if (requirementChannels.length === 0) {
    requirementsHtml = '<p class="text-secondary">No attribute requirements configured.</p>';
  } else {
    requirementsHtml = `
      <table class="inspector-requirements-table">
        <thead>
          <tr>
            <th>Channel</th>
            <th>Required Attributes</th>
          </tr>
        </thead>
        <tbody>
          ${requirementChannels
            .map((channel) => {
              const required = Array.isArray(requirements[channel]) ? requirements[channel] : [];
              const requiredBadges = required.length
                ? required.map((code: string) => `<span class="badge badge-success inspector-attr-code">${escapeHtml(code)}</span>`).join(' ')
                : '<span class="text-secondary">None</span>';
              return `
                <tr>
                  <td class="mono">${escapeHtml(channel)}</td>
                  <td>${requiredBadges}</td>
                </tr>
              `;
            })
            .join('')}
        </tbody>
      </table>
    `;
  }

  let variantsHtml = '';
  if (variants.length === 0) {
    variantsHtml = `
      <div class="inspector-section">
        <div class="inspector-section-title">🧬 Family Variants</div>
        <p class="text-secondary">No variants configured for this family.</p>
      </div>
    `;
  } else {
    const variantsRows = variants
      .map((variant: any) => {
        const variantLabels = renderLocalizedLabels(variant.labels);
        const axes = Array.isArray(variant.variant_attribute_sets)
          ? variant.variant_attribute_sets
              .map((set: any) => {
                const level = set.level ?? '?';
                const axesList = Array.isArray(set.axes) ? set.axes : [];
                const axesBadges = axesList.length
                  ? axesList.map((axis: string) => `<span class="badge badge-accent inspector-attr-code">${escapeHtml(axis)}</span>`).join(' ')
                  : '<span class="text-secondary">None</span>';
                return `<div class="inspector-variant-level"><span class="inspector-level">Level ${escapeHtml(String(level))}</span> ${axesBadges}</div>`;
              })
              .join('')
          : '<span class="text-secondary">No axes</span>';

        return `
          <div class="inspector-variant-card">
            <div class="inspector-variant-header">
              <span class="mono">${escapeHtml(variant.code)}</span>
              <span class="badge badge-info">${Array.isArray(variant.variant_attribute_sets) ? variant.variant_attribute_sets.length : 0} levels</span>
            </div>
            <div class="inspector-variant-body">
              <div class="inspector-variant-labels">${variantLabels}</div>
              <div class="inspector-variant-axes">${axes}</div>
            </div>
          </div>
        `;
      })
      .join('');

    variantsHtml = `
      <div class="inspector-section">
        <div class="inspector-section-title">🧬 Family Variants <span class="badge badge-info">${variants.length}</span></div>
        <div class="inspector-variants-list">${variantsRows}</div>
      </div>
    `;
  }

  return `
    <div class="entity-inspector-rich">
      <div class="inspector-section">
        <div class="inspector-section-title">👔 Family Definition</div>
        <div class="inspector-row">
          <span class="inspector-label">Code</span>
          <span class="inspector-value mono">${escapeHtml(payload.code)}</span>
        </div>
        <div class="inspector-row">
          <span class="inspector-label">Labels</span>
          <span class="inspector-value">${labels}</span>
        </div>
      </div>

      <div class="inspector-section">
        <div class="inspector-section-title">📦 Attributes <span class="badge badge-neutral">${attributes.length}</span></div>
        <div class="inspector-attributes-cloud">${attributesHtml}</div>
      </div>

      <div class="inspector-section">
        <div class="inspector-section-title">🔐 Attribute Requirements</div>
        ${requirementsHtml}
      </div>

      ${variantsHtml}
    </div>
  `;
}

function renderCategoryInspector(payload: any, tree: CategoryTreeNode[] = []): string {
  const labels = renderLocalizedLabels(payload.labels);

  function findPath(nodes: CategoryTreeNode[], targetCode: string, path: CategoryTreeNode[] = []): CategoryTreeNode[] | null {
    for (const node of nodes) {
      if (node.code === targetCode) {
        return [...path, node];
      }
      if (node.children && node.children.length > 0) {
        const found = findPath(node.children, targetCode, [...path, node]);
        if (found) return found;
      }
    }
    return null;
  }

  function renderTreeNodes(nodes: CategoryTreeNode[], currentCode: string, depth = 0): string {
    return nodes
      .map((node) => {
        const isCurrent = node.code === currentCode;
        const hasChildren = node.children && node.children.length > 0;
        const nodeLabel = node.labels?.en_US || node.label || node.code;
        const childrenHtml = hasChildren
          ? `<div class="inspector-tree-children">${renderTreeNodes(node.children || [], currentCode, depth + 1)}</div>`
          : '';

        return `
          <div class="inspector-tree-node ${isCurrent ? 'inspector-tree-current' : ''}" style="--tree-depth: ${depth};">
            <div class="inspector-tree-node-content">
              ${hasChildren ? '<span class="inspector-tree-toggle">▾</span>' : '<span class="inspector-tree-spacer"></span>'}
              <span class="inspector-tree-icon">🌲</span>
              <span class="inspector-tree-code mono">${escapeHtml(node.code)}</span>
              <span class="inspector-tree-label">${escapeHtml(nodeLabel)}</span>
              ${isCurrent ? '<span class="badge badge-accent">current</span>' : ''}
            </div>
            ${childrenHtml}
          </div>
        `;
      })
      .join('');
  }

  const path = findPath(tree, payload.code);
  const breadcrumbHtml = path
    ? path
        .map((node) => {
          const nodeLabel = node.labels?.en_US || node.label || node.code;
          const isCurrent = node.code === payload.code;
          return `<span class="inspector-breadcrumb-item ${isCurrent ? 'inspector-breadcrumb-current' : ''}">${escapeHtml(nodeLabel)}${!isCurrent ? ' <span class="inspector-breadcrumb-sep">›</span>' : ''}</span>`;
        })
        .join(' ')
    : `<span class="inspector-breadcrumb-item inspector-breadcrumb-current">${escapeHtml(payload.code)}</span>`;

  const treeHtml = tree.length
    ? renderTreeNodes(tree, payload.code)
    : '<p class="text-secondary">No category hierarchy available.</p>';

  return `
    <div class="entity-inspector-rich">
      <div class="inspector-section">
        <div class="inspector-section-title">🌲 Category Definition</div>
        <div class="inspector-row">
          <span class="inspector-label">Code</span>
          <span class="inspector-value mono">${escapeHtml(payload.code)}</span>
        </div>
        <div class="inspector-row">
          <span class="inspector-label">Parent</span>
          <span class="inspector-value mono">${payload.parent ? escapeHtml(payload.parent) : '<span class="badge badge-success">Root</span>'}</span>
        </div>
        <div class="inspector-row">
          <span class="inspector-label">Labels</span>
          <span class="inspector-value">${labels}</span>
        </div>
      </div>

      <div class="inspector-section">
        <div class="inspector-section-title">🛤️ Hierarchy Path</div>
        <div class="inspector-breadcrumb">${breadcrumbHtml}</div>
      </div>

      <div class="inspector-section">
        <div class="inspector-section-title">🌳 Category Tree</div>
        <div class="inspector-tree">${treeHtml}</div>
      </div>
    </div>
  `;
}

function renderMediaFileInspector(payload: any): string {
  const code = payload.code || '—';
  const filename = payload.original_filename || code;
  const mime = payload.mime_type || '—';
  const size = typeof payload.size === 'number' ? formatFileSize(payload.size) : '—';
  const extension = payload.extension || '—';
  const dimensions = payload.dimensions
    ? `${payload.dimensions.width}×${payload.dimensions.height}`
    : '—';
  const downloadHref = payload._links?.download?.href || '';

  const downloadLink = downloadHref
    ? `<a href="${escapeHtml(downloadHref)}" target="_blank" rel="noopener noreferrer" class="btn btn-sm btn-secondary">
        Download File
      </a>`
    : '';

  return `
    <div class="entity-inspector-rich">
      <div class="inspector-section">
        <div class="inspector-section-title">📎 Media File Details</div>
        <div class="inspector-row">
          <span class="inspector-label">Code</span>
          <span class="inspector-value mono">${escapeHtml(code)}</span>
        </div>
        <div class="inspector-row">
          <span class="inspector-label">Original Filename</span>
          <span class="inspector-value mono">${escapeHtml(filename)}</span>
        </div>
        <div class="inspector-row">
          <span class="inspector-label">MIME Type</span>
          <span class="inspector-value"><span class="badge badge-info">${escapeHtml(mime)}</span></span>
        </div>
        <div class="inspector-row">
          <span class="inspector-label">File Size</span>
          <span class="inspector-value mono">${escapeHtml(size)}</span>
        </div>
        <div class="inspector-row">
          <span class="inspector-label">Extension</span>
          <span class="inspector-value mono">${escapeHtml(extension)}</span>
        </div>
        <div class="inspector-row">
          <span class="inspector-label">Dimensions</span>
          <span class="inspector-value mono">${escapeHtml(dimensions)}</span>
        </div>
        ${downloadLink ? `<div class="inspector-row"><span class="inspector-label">Download</span><span class="inspector-value">${downloadLink}</span></div>` : ''}
      </div>
    </div>
  `;
}

function renderAssetFamilyInspector(payload: any): string {
  const code = payload.code || '—';
  const labels = renderLocalizedLabels(payload.labels);
  const mainMedia = payload.attribute_as_main_media
    ? `<span class="badge badge-accent">${escapeHtml(payload.attribute_as_main_media)}</span>`
    : '<span class="text-secondary">—</span>';
  const naming = payload.naming_convention
    ? `<pre class="code-block"><code>${escapeHtml(JSON.stringify(payload.naming_convention, null, 2))}</code></pre>`
    : '<span class="text-secondary">No naming convention configured.</span>';

  const attributes = Array.isArray(payload.attributes) ? payload.attributes : [];
  let attributesHtml = '';
  if (attributes.length === 0) {
    attributesHtml = '<p class="text-secondary">No asset attributes defined for this family.</p>';
  } else {
    const rows = attributes
      .map((attr: any) => {
        const attrCode = attr.code || '—';
        const attrType = attr.type || '—';
        const attrLabels = renderLocalizedLabels(attr.labels);
        const flags = [];
        if (attr.localizable) flags.push('Localizable');
        if (attr.scopable) flags.push('Scopable');
        const flagsHtml = flags.length
          ? flags.map((f) => `<span class="badge badge-neutral">${escapeHtml(f)}</span>`).join(' ')
          : '<span class="text-secondary">—</span>';
        return `
          <tr>
            <td class="mono">${escapeHtml(attrCode)}</td>
            <td><span class="badge ${getAttributeTypeBadgeClass(attrType)}">${escapeHtml(attrType)}</span></td>
            <td>${attrLabels}</td>
            <td>${flagsHtml}</td>
          </tr>
        `;
      })
      .join('');
    attributesHtml = `
      <table class="data-table">
        <thead>
          <tr>
            <th>Attribute Code</th>
            <th>Type</th>
            <th>Labels</th>
            <th>Flags</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
    `;
  }

  const transformationCount = Array.isArray(payload.transformations) ? payload.transformations.length : 0;

  return `
    <div class="entity-inspector-rich">
      <div class="inspector-section">
        <div class="inspector-section-title">🖼️ Asset Family Definition</div>
        <div class="inspector-row">
          <span class="inspector-label">Code</span>
          <span class="inspector-value mono">${escapeHtml(code)}</span>
        </div>
        <div class="inspector-row">
          <span class="inspector-label">Labels</span>
          <span class="inspector-value">${labels}</span>
        </div>
        <div class="inspector-row">
          <span class="inspector-label">Main Media Attribute</span>
          <span class="inspector-value">${mainMedia}</span>
        </div>
        <div class="inspector-row">
          <span class="inspector-label">Transformations</span>
          <span class="inspector-value mono">${transformationCount}</span>
        </div>
      </div>

      <div class="inspector-section">
        <div class="inspector-section-title">🏷️ Naming Convention</div>
        ${naming}
      </div>

      <div class="inspector-section">
        <div class="inspector-section-title">🎨 Source Akeneo Asset Attributes <span class="badge badge-neutral">${attributes.length}</span></div>
        ${attributesHtml}
      </div>
    </div>
  `;
}

function renderReferenceEntityInspector(payload: any): string {
  const code = payload.code || '—';
  const labels = renderLocalizedLabels(payload.labels);
  const imageHref = payload._links?.image_download?.href || payload.image || '';
  const imageLink = imageHref
    ? `<a href="${escapeHtml(imageHref)}" target="_blank" rel="noopener noreferrer" class="btn btn-sm btn-secondary">View Image</a>`
    : '<span class="text-secondary">No image</span>';

  const attributes = Array.isArray(payload.attributes) ? payload.attributes : [];
  let attributesHtml = '';
  if (attributes.length === 0) {
    attributesHtml = '<p class="text-secondary">No reference entity attributes defined.</p>';
  } else {
    const rows = attributes
      .map((attr: any) => {
        const attrCode = attr.code || '—';
        const attrType = attr.type || '—';
        const attrLabels = renderLocalizedLabels(attr.labels);
        const flags = [];
        if (attr.value_per_locale) flags.push('Localizable');
        if (attr.value_per_channel) flags.push('Channel specific');
        if (attr.is_required_for_completeness) flags.push('Required');
        const flagsHtml = flags.length
          ? flags.map((f) => `<span class="badge badge-neutral">${escapeHtml(f)}</span>`).join(' ')
          : '<span class="text-secondary">—</span>';
        return `
          <tr>
            <td class="mono">${escapeHtml(attrCode)}</td>
            <td><span class="badge ${getAttributeTypeBadgeClass(attrType)}">${escapeHtml(attrType)}</span></td>
            <td>${attrLabels}</td>
            <td>${flagsHtml}</td>
          </tr>
        `;
      })
      .join('');
    attributesHtml = `
      <table class="data-table">
        <thead>
          <tr>
            <th>Attribute Code</th>
            <th>Type</th>
            <th>Labels</th>
            <th>Flags</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
    `;
  }

  return `
    <div class="entity-inspector-rich">
      <div class="inspector-section">
        <div class="inspector-section-title">📚 Reference Entity Definition</div>
        <div class="inspector-row">
          <span class="inspector-label">Code</span>
          <span class="inspector-value mono">${escapeHtml(code)}</span>
        </div>
        <div class="inspector-row">
          <span class="inspector-label">Labels</span>
          <span class="inspector-value">${labels}</span>
        </div>
        <div class="inspector-row">
          <span class="inspector-label">Image</span>
          <span class="inspector-value">${imageLink}</span>
        </div>
      </div>

      <div class="inspector-section">
        <div class="inspector-section-title">🏷️ Reference Entity Attributes <span class="badge badge-neutral">${attributes.length}</span></div>
        ${attributesHtml}
      </div>
    </div>
  `;
}

function renderReferenceRecordValueData(data: unknown): string {
  if (data === null || data === undefined) return '<span class="text-secondary">—</span>';
  if (typeof data === 'boolean') return data ? 'Yes' : 'No';
  if (typeof data === 'string') return escapeHtml(data);
  if (Array.isArray(data)) {
    return data.map((v) => (typeof v === 'string' ? escapeHtml(v) : escapeHtml(JSON.stringify(v)))).join(', ');
  }
  return escapeHtml(JSON.stringify(data));
}

function renderReferenceRecordInspector(payload: any): string {
  const code = payload.code || '—';
  const values = payload.values || {};
  const valueEntries = Object.entries(values).filter(([_, items]) => items !== undefined && items !== null);

  let valuesHtml = '';
  if (valueEntries.length === 0) {
    valuesHtml = '<p class="text-secondary">No record values defined.</p>';
  } else {
    const rows = valueEntries
      .map(([attribute, items]: [string, any]) => {
        const rowsForAttr = (Array.isArray(items) ? items : [items])
          .map((item: any) => {
            const locale = item?.locale ? escapeHtml(item.locale) : '<span class="text-secondary">—</span>';
            const channel = item?.channel ? escapeHtml(item.channel) : '<span class="text-secondary">—</span>';
            const data = renderReferenceRecordValueData(item?.data);
            return `
              <tr>
                <td class="mono">${escapeHtml(attribute)}</td>
                <td>${locale}</td>
                <td>${channel}</td>
                <td>${data}</td>
              </tr>
            `;
          })
          .join('');
        return rowsForAttr;
      })
      .join('');
    valuesHtml = `
      <table class="data-table">
        <thead>
          <tr>
            <th>Attribute</th>
            <th>Locale</th>
            <th>Channel</th>
            <th>Value</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
    `;
  }

  return `
    <div class="entity-inspector-rich">
      <div class="inspector-section">
        <div class="inspector-section-title">📝 Reference Record Definition</div>
        <div class="inspector-row">
          <span class="inspector-label">Code</span>
          <span class="inspector-value mono">${escapeHtml(code)}</span>
        </div>
        <div class="inspector-row">
          <span class="inspector-label">Created</span>
          <span class="inspector-value mono">${payload.created ? escapeHtml(payload.created) : '—'}</span>
        </div>
        <div class="inspector-row">
          <span class="inspector-label">Updated</span>
          <span class="inspector-value mono">${payload.updated ? escapeHtml(payload.updated) : '—'}</span>
        </div>
      </div>

      <div class="inspector-section">
        <div class="inspector-section-title">🧩 Record Values <span class="badge badge-neutral">${valueEntries.length}</span></div>
        ${valuesHtml}
      </div>
    </div>
  `;
}

function renderMeasurementFamilyInspector(payload: any): string {
  const code = payload.code || '—';
  const labels = renderLocalizedLabels(payload.labels);
  const standardUnit = payload.standard_unit_code || payload.standard || '—';
  const units = Array.isArray(payload.units) ? payload.units : [];

  let unitsHtml = '';
  if (units.length === 0) {
    unitsHtml = '<p class="text-secondary">No units defined for this family.</p>';
  } else {
    const rows = units
      .map((unit: any) => {
        const unitCode = unit.code || '—';
        const unitLabels = renderLocalizedLabels(unit.labels);
        const symbol = unit.symbol || '—';
        const convert = unit.convert || {};
        const operator = convert.operator || Object.keys(convert)[0] || '—';
        const value = convert.value !== undefined ? convert.value : Object.values(convert)[0] || '—';
        const isStandard = unitCode === standardUnit;
        return `
          <tr>
            <td class="mono">${escapeHtml(unitCode)} ${isStandard ? '<span class="badge badge-success">standard</span>' : ''}</td>
            <td>${unitLabels}</td>
            <td class="mono">${escapeHtml(symbol)}</td>
            <td class="mono">${escapeHtml(String(operator))} ${escapeHtml(String(value))}</td>
          </tr>
        `;
      })
      .join('');
    unitsHtml = `
      <table class="data-table">
        <thead>
          <tr>
            <th>Unit Code</th>
            <th>Labels</th>
            <th>Symbol</th>
            <th>Conversion</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
    `;
  }

  return `
    <div class="entity-inspector-rich">
      <div class="inspector-section">
        <div class="inspector-section-title">📏 Measurement Family Definition</div>
        <div class="inspector-row">
          <span class="inspector-label">Code</span>
          <span class="inspector-value mono">${escapeHtml(code)}</span>
        </div>
        <div class="inspector-row">
          <span class="inspector-label">Labels</span>
          <span class="inspector-value">${labels}</span>
        </div>
        <div class="inspector-row">
          <span class="inspector-label">Standard Unit</span>
          <span class="inspector-value mono">${escapeHtml(String(standardUnit))}</span>
        </div>
      </div>

      <div class="inspector-section">
        <div class="inspector-section-title">📐 Units <span class="badge badge-neutral">${units.length}</span></div>
        ${unitsHtml}
      </div>
    </div>
  `;
}

function renderUserInspector(payload: any): string {
  const username = payload.username || '—';
  const firstName = payload.first_name || '';
  const lastName = payload.last_name || '';
  const fullName = `${firstName} ${lastName}`.trim() || username;
  const email = payload.email || '—';
  const enabled = payload.enabled !== false;
  const roles = Array.isArray(payload.roles) ? payload.roles : [];
  const userGroups = Array.isArray(payload.user_groups) ? payload.user_groups : [];

  const rolesHtml = roles.length
    ? roles.map((r: string) => `<span class="badge badge-accent">${escapeHtml(r)}</span>`).join(' ')
    : '<span class="text-secondary">—</span>';
  const groupsHtml = userGroups.length
    ? userGroups.map((g: string) => `<span class="badge badge-neutral">${escapeHtml(g)}</span>`).join(' ')
    : '<span class="text-secondary">—</span>';

  return `
    <div class="entity-inspector-rich">
      <div class="inspector-section">
        <div class="inspector-section-title">👤 User Profile</div>
        <div class="inspector-row">
          <span class="inspector-label">Username</span>
          <span class="inspector-value mono">${escapeHtml(username)}</span>
        </div>
        <div class="inspector-row">
          <span class="inspector-label">Full Name</span>
          <span class="inspector-value">${escapeHtml(fullName)}</span>
        </div>
        <div class="inspector-row">
          <span class="inspector-label">Email</span>
          <span class="inspector-value mono">${escapeHtml(email)}</span>
        </div>
        <div class="inspector-row">
          <span class="inspector-label">Status</span>
          <span class="inspector-value">${enabled ? '<span class="badge badge-success">Enabled</span>' : '<span class="badge badge-neutral">Disabled</span>'}</span>
        </div>
        <div class="inspector-row">
          <span class="inspector-label">Default Locale</span>
          <span class="inspector-value mono">${escapeHtml(payload.default_locale || '—')}</span>
        </div>
        <div class="inspector-row">
          <span class="inspector-label">Catalog Scope</span>
          <span class="inspector-value mono">${escapeHtml(payload.catalog_default_scope || '—')}</span>
        </div>
      </div>

      <div class="inspector-section">
        <div class="inspector-section-title">🔐 Roles & Groups</div>
        <div class="inspector-row">
          <span class="inspector-label">Roles <span class="badge badge-neutral">${roles.length}</span></span>
          <span class="inspector-value">${rolesHtml}</span>
        </div>
        <div class="inspector-row">
          <span class="inspector-label">User Groups <span class="badge badge-neutral">${userGroups.length}</span></span>
          <span class="inspector-value">${groupsHtml}</span>
        </div>
      </div>
    </div>
  `;
}

export interface CascadeDependencyInfo {
  type: string;
  id: string;
  label?: string;
  depth?: number;
  scope?: string;
  isDirectRequired?: boolean;
  reason?: string;
  existsOnSource: boolean;
  existsOnTarget: boolean;
  status: 'ready' | 'missing' | 'not_found_on_source';
}

export interface BlastRadiusSummaryInfo {
  maxDepth: number;
  totalCount: number;
  totalMissingCount: number;
  directRequiredCount: number;
  missingDirectCount: number;
  extendedSchemaCount: number;
  missingExtendedCount: number;
  recommendedStrategy: 'lean' | 'full' | 'custom';
}

export interface CascadeResolutionInfo {
  success: boolean;
  targetEntity: { type: string; id: string };
  existsOnSource: boolean;
  existsOnTarget: boolean;
  canSyncDirectly: boolean;
  missingCount: number;
  totalDependencyCount: number;
  blastRadius?: BlastRadiusSummaryInfo;
  dependencies: CascadeDependencyInfo[];
  orderedStages: Array<{ stage: number; entityType: string; items: string[]; isPrerequisite: boolean }>;
}

export let activeInspectResolution: CascadeResolutionInfo | null = null;
export let activeInspectStrategy: 'lean' | 'full' | 'custom' = 'lean';
export let activeDiffResolution: CascadeResolutionInfo | null = null;
export let activeDiffStrategy: 'lean' | 'full' | 'custom' = 'lean';

export async function fetchEntityDependencies(entityType: string, entityId: string): Promise<CascadeResolutionInfo | null> {
  try {
    const res = await fetch(`/api/v1/sync/dependencies/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}`);
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

export function renderDependenciesCard(
  panelEl: HTMLElement | null,
  badgeEl: HTMLElement | null,
  _listEl: HTMLElement | null,
  hintEl: HTMLElement | null,
  resolution: CascadeResolutionInfo | null,
  entityType: string,
  entityId: string,
  mode: 'inspect' | 'diff' = 'inspect'
): void {
  if (!panelEl) return;
  panelEl.style.display = 'block';

  if (mode === 'inspect') {
    activeInspectResolution = resolution;
    activeInspectStrategy = resolution?.blastRadius?.recommendedStrategy || 'lean';
  } else {
    activeDiffResolution = resolution;
    activeDiffStrategy = resolution?.blastRadius?.recommendedStrategy || 'lean';
  }

  const prefix = mode === 'inspect' ? 'entity-inspect' : 'entity-diff';
  const loadingEl = document.getElementById(`${prefix}-analysis-loading`);
  const contentEl = document.getElementById(`${prefix}-analysis-content`);
  const blastMetricsEl = document.getElementById(`${prefix}-blast-metrics`);
  const treeContainer = document.getElementById(`${prefix}-tree-container`);
  const btnLean = document.getElementById(`btn-${mode}-strategy-lean`);
  const btnFull = document.getElementById(`btn-${mode}-strategy-full`);
  const btnCustom = document.getElementById(`btn-${mode}-strategy-custom`);
  const syncBtn = document.getElementById(mode === 'inspect' ? 'btn-cascade-sync-inspect' : 'btn-cascade-sync-diff');

  if (loadingEl) loadingEl.style.display = 'none';
  if (contentEl) contentEl.style.display = 'block';
  if (syncBtn) syncBtn.removeAttribute('disabled');

  if (!resolution || !Array.isArray(resolution.dependencies)) {
    if (badgeEl) badgeEl.innerHTML = '<span class="badge badge-neutral">Prerequisite check unavailable</span>';
    if (hintEl) hintEl.textContent = 'Could not inspect target prerequisites.';
    if (treeContainer) treeContainer.innerHTML = '<div class="text-secondary" style="font-size: 0.8rem;">Tree unavailable</div>';
    return;
  }

  const br = resolution.blastRadius;
  const missing = resolution.dependencies.filter((d) => !d.existsOnTarget);

  if (badgeEl) {
    if (resolution.dependencies.length === 0) {
      badgeEl.innerHTML = '<span class="badge badge-success">No External Dependencies</span>';
    } else if (missing.length === 0) {
      badgeEl.innerHTML = `<span class="badge badge-success">✓ All ${resolution.dependencies.length} Prerequisites Ready</span>`;
    } else {
      badgeEl.innerHTML = `<span class="badge badge-warning">⚠️ ${missing.length} Missing of ${resolution.dependencies.length}</span>`;
    }
  }

  if (blastMetricsEl && br) {
    blastMetricsEl.innerHTML = `
      <span class="badge badge-neutral" title="Deepest dependency level">Depth: ${br.maxDepth || 1} Levels</span>
      <span class="badge badge-accent" title="Directly referenced & required by this product">Direct: ${br.directRequiredCount} (${br.missingDirectCount} Missing)</span>
      <span class="badge badge-secondary" title="Unused family schema attributes">Extended: ${br.extendedSchemaCount} (${br.missingExtendedCount} Missing)</span>
    `;
  }

  const renderTree = (currentStrategy: 'lean' | 'full' | 'custom') => {
    if (!treeContainer) return;

    if (resolution.dependencies.length === 0) {
      treeContainer.innerHTML = '<div class="text-secondary" style="padding: 0.25rem;">This entity is a root node and has no external prerequisites.</div>';
      return;
    }

    const directItems = resolution.dependencies.filter((d) => d.isDirectRequired || d.depth === 1);
    const extendedItems = resolution.dependencies.filter((d) => !d.isDirectRequired && d.depth !== 1);

    const renderItemRow = (dep: CascadeDependencyInfo) => {
      const isMissing = !dep.existsOnTarget;
      const isChecked = currentStrategy === 'full' 
        ? isMissing 
        : currentStrategy === 'lean' 
        ? (isMissing && dep.isDirectRequired) 
        : isMissing;

      const disabledAttr = !isMissing ? 'disabled' : '';
      const checkedAttr = isChecked ? 'checked' : '';
      const statusBadge = isMissing 
        ? '<span class="badge badge-warning" style="font-size: 0.7rem; padding: 1px 4px;">⚠️ Missing</span>' 
        : '<span class="badge badge-success" style="font-size: 0.7rem; padding: 1px 4px;">✓ Ready</span>';

      return `
        <div style="display: flex; align-items: center; justify-content: space-between; padding: 3px 0; border-bottom: 1px solid var(--border-color-subtle, rgba(255,255,255,0.05));">
          <div style="display: flex; align-items: center; gap: 0.4rem; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
            <input type="checkbox" class="cascade-tree-checkbox" data-mode="${mode}" data-type="${escapeHtml(dep.type)}" data-id="${escapeHtml(dep.id)}" ${checkedAttr} ${disabledAttr} style="cursor: pointer; margin: 0;">
            <span class="badge badge-outline" style="font-size: 0.7rem; padding: 1px 4px;">${escapeHtml(dep.type)}</span>
            <strong style="font-size: 0.8rem;">${escapeHtml(dep.id)}</strong>
            <span class="text-secondary" style="font-size: 0.72rem;" title="${escapeHtml(dep.reason || '')}">(${escapeHtml(dep.reason || (dep.isDirectRequired ? 'Direct required' : 'Family schema'))})</span>
          </div>
          <div>${statusBadge}</div>
        </div>
      `;
    };

    let html = '';
    if (directItems.length > 0) {
      html += `
        <div style="margin-bottom: 0.6rem;">
          <div style="font-weight: 600; font-size: 0.75rem; color: var(--accent-primary); text-transform: uppercase; margin-bottom: 0.2rem;">
            📌 Level 1: Direct Required Prerequisites (${directItems.length} items)
          </div>
          ${directItems.map(renderItemRow).join('')}
        </div>
      `;
    }

    if (extendedItems.length > 0) {
      html += `
        <div>
          <div style="font-weight: 600; font-size: 0.75rem; color: var(--text-secondary); text-transform: uppercase; margin-bottom: 0.2rem;">
            🌳 Level 2+: Extended Family Schema (${extendedItems.length} items — optional for this single product)
          </div>
          ${extendedItems.map(renderItemRow).join('')}
        </div>
      `;
    }

    treeContainer.innerHTML = html;

    treeContainer.querySelectorAll('.cascade-tree-checkbox').forEach((cb) => {
      cb.addEventListener('change', () => {
        if (mode === 'inspect') activeInspectStrategy = 'custom';
        else activeDiffStrategy = 'custom';
        updateStrategyButtons('custom');
      });
    });
  };

  const updateStrategyButtons = (strat: 'lean' | 'full' | 'custom') => {
    [btnLean, btnFull, btnCustom].forEach((b) => {
      if (b) {
        b.classList.remove('btn-primary', 'active-strategy');
        b.classList.add('btn-outline');
      }
    });

    const activeBtn = strat === 'lean' ? btnLean : strat === 'full' ? btnFull : btnCustom;
    if (activeBtn) {
      activeBtn.classList.remove('btn-outline');
      activeBtn.classList.add('btn-primary', 'active-strategy');
    }

    if (btnLean) btnLean.textContent = `${strat === 'lean' ? '●' : '○'} Lean Sync (Minimal Prerequisites)`;
    if (btnFull) btnFull.textContent = `${strat === 'full' ? '●' : '○'} Full Schema Sync (All 32+ family attributes)`;
    if (btnCustom) btnCustom.textContent = `${strat === 'custom' ? '●' : '○'} Custom Checklist`;

    if (syncBtn) {
      const selected = getSelectedTreeItems(mode);
      const count = Object.values(selected).reduce((acc, list) => acc + list.length, 0);
      syncBtn.textContent = `⚡ Start Sync (${count > 0 ? `${count} Prerequisites + ` : ''}${entityType})`;
    }
  };

  if (btnLean) {
    btnLean.onclick = () => {
      if (mode === 'inspect') activeInspectStrategy = 'lean';
      else activeDiffStrategy = 'lean';
      renderTree('lean');
      updateStrategyButtons('lean');
    };
  }

  if (btnFull) {
    btnFull.onclick = () => {
      if (mode === 'inspect') activeInspectStrategy = 'full';
      else activeDiffStrategy = 'full';
      renderTree('full');
      updateStrategyButtons('full');
    };
  }

  if (btnCustom) {
    btnCustom.onclick = () => {
      if (mode === 'inspect') activeInspectStrategy = 'custom';
      else activeDiffStrategy = 'custom';
      updateStrategyButtons('custom');
    };
  }

  const initialStrategy = mode === 'inspect' ? activeInspectStrategy : activeDiffStrategy;
  renderTree(initialStrategy);
  updateStrategyButtons(initialStrategy);

  if (hintEl) {
    if (missing.length > 0) {
      hintEl.innerHTML = `💡 <strong>Impact Analysis:</strong> Total blast radius is <strong>${resolution.dependencies.length} items</strong> (${br?.missingDirectCount || 0} direct required missing, ${br?.missingExtendedCount || 0} extended family attributes missing). Use <strong>Lean Sync</strong> to sync only what this product needs.`;
    } else {
      hintEl.innerHTML = `✅ All prerequisite dependencies exist on target. This ${escapeHtml(entityType)} can be safely synced.`;
    }
  }
}

export function getSelectedTreeItems(mode: 'inspect' | 'diff'): Record<string, string[]> {
  if (typeof document === 'undefined') return {};
  const checkboxes = document.querySelectorAll(`.cascade-tree-checkbox[data-mode="${mode}"]:checked`) as NodeListOf<HTMLInputElement>;
  const result: Record<string, string[]> = {};

  checkboxes.forEach((cb) => {
    const type = cb.getAttribute('data-type');
    const id = cb.getAttribute('data-id');
    if (type && id) {
      if (!result[type]) result[type] = [];
      result[type].push(id);
    }
  });

  return result;
}

export async function executeEntityCascadeSync(
  entityType: string,
  entityId: string,
  strategy?: 'lean' | 'full' | 'custom',
  selectedItems?: Record<string, string[]>
): Promise<void> {
  const activeStrat = strategy || 'lean';

  // 1. Immediately disable sync buttons with active loading spinner
  const inspectSyncBtn = document.getElementById('btn-cascade-sync-inspect');
  const diffSyncBtn = document.getElementById('btn-cascade-sync-diff');
  if (inspectSyncBtn) {
    inspectSyncBtn.setAttribute('disabled', 'true');
    inspectSyncBtn.innerHTML = '<span class="spinner-sm"></span> <span>Launching Sync...</span>';
  }
  if (diffSyncBtn) {
    diffSyncBtn.setAttribute('disabled', 'true');
    diffSyncBtn.innerHTML = '<span class="spinner-sm"></span> <span>Launching Sync...</span>';
  }

  // 2. Immediately close inspecting/diff modals to prevent duplicate interactions
  const inspectModal = document.getElementById('entity-inspect-modal');
  const diffModal = document.getElementById('entity-diff-modal');
  if (inspectModal) inspectModal.style.display = 'none';
  if (diffModal) diffModal.style.display = 'none';

  // 3. Immediately open Batch Progress Modal in Initializing State
  openBatchProgressModal(`initializing-${entityId}`, false);
  const title = document.getElementById('batch-progress-title');
  const subtitle = document.getElementById('batch-progress-subtitle');
  const stage = document.getElementById('batch-modal-stage-label');
  if (title) title.textContent = `Cascading Sync: ${entityId}`;
  if (subtitle) subtitle.textContent = `Queuing ${activeStrat} scope sync job with prerequisite DAG resolution...`;
  if (stage) stage.textContent = `Resolving dependency graph on target PIM...`;

  try {
    const payload: any = {
      async: true,
      strategy: activeStrat,
    };

    if (selectedItems && Object.keys(selectedItems).length > 0) {
      payload.selected_items = selectedItems;
    }

    const res = await fetch(`/api/v1/sync/cascade/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      const errData = await res.json().catch(() => ({}));
      throw new Error(errData.message || `HTTP ${res.status}`);
    }

    const data = await res.json();
    const jobId = data.job_id || data.jobId;

    if (jobId) {
      openBatchProgressModal(jobId, false);
      const prereqs = data.missing_prerequisites_count ?? 0;
      if (prereqs > 0) {
        showToast(`Cascading sync job queued with ${prereqs} prerequisite dependencies (${activeStrat} mode)`, 'success', 4000);
      } else {
        showToast(`Sync job ${jobId} queued`, 'success', 3000);
      }
    }
  } catch (err: any) {
    if (stage) stage.textContent = `Failed to queue sync: ${err.message || String(err)}`;
    showToast(`Cascade sync failed: ${err.message || String(err)}`, 'error', 5000);
  }
}

export async function inspectExplorerEntity(entityId: string): Promise<void> {
  const modal = document.getElementById('entity-inspect-modal');
  const title = document.getElementById('entity-inspect-title');
  const subtitle = document.getElementById('entity-inspect-subtitle');
  const tags = document.getElementById('entity-inspect-tags');
  const codeEl = document.getElementById('entity-inspect-json-code');
  const depsPanel = document.getElementById('entity-inspect-deps-panel');
  const depsBadge = document.getElementById('entity-inspect-deps-badge');
  const treeContainer = document.getElementById('entity-inspect-tree-container');
  const depsHint = document.getElementById('entity-inspect-deps-hint');

  if (!modal) return;

  const meta = getExplorerTypeMeta();
  const entityType = state.explorer.entityType;
  if (title) title.textContent = `${meta.label} Inspector`;
  if (subtitle) subtitle.textContent = `Inspect entity payload & values`;
  if (tags) {
    tags.innerHTML = `
      <span class="badge badge-accent">${escapeHtml(meta.label)}</span>
      <span class="badge badge-neutral mono">${escapeHtml(entityId)}</span>
    `;
  }
  if (codeEl) codeEl.innerHTML = '<code>Loading entity details from source...</code>';

  if (depsPanel) {
    depsPanel.style.display = 'block';
    const loadingEl = document.getElementById('entity-inspect-analysis-loading');
    const contentEl = document.getElementById('entity-inspect-analysis-content');
    if (loadingEl) loadingEl.style.display = 'flex';
    if (contentEl) contentEl.style.display = 'none';

    const syncBtn = document.getElementById('btn-cascade-sync-inspect');
    if (syncBtn) {
      syncBtn.setAttribute('disabled', 'true');
      syncBtn.innerHTML = '<span class="spinner-sm"></span> <span>Analyzing Prerequisites...</span>';
    }
  }

  modal.style.display = 'flex';

  try {
    const res = await fetch(
      `/api/v1/discovery/entities/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}`
    );
    if (!res.ok) {
      const errData = await res.json().catch(() => ({}));
      const err: any = new Error(errData.message || `HTTP ${res.status}`);
      err.status = res.status;
      err.data = errData;
      throw err;
    }
    const data = await res.json();
    const payload = data.item || data;

    state.batch.inspectEntity = { entityType, entityId, payload };

    if (tags && isProductLikeEntityType(entityType)) {
      const commercialName = resolveProductCommercialName(payload);
      if (commercialName) {
        tags.innerHTML = `
          <span class="badge badge-accent">${escapeHtml(meta.label)}</span>
          <span class="badge badge-info">${escapeHtml(commercialName)}</span>
          <span class="badge badge-neutral mono">${escapeHtml(entityId)}</span>
        `;
      }
    }

    let richHtml: string | null = null;

    if (entityType === 'attributes') {
      const optionsRes = await fetch(
        `/api/v1/discovery/attributes/${encodeURIComponent(entityId)}/options?limit=100`
      );
      const optionsData = optionsRes.ok ? await optionsRes.json() : {};
      const options = Array.isArray(optionsData.items) ? optionsData.items : [];
      richHtml = renderAttributeInspector(payload, options);
    } else if (entityType === 'families') {
      const variantsRes = await fetch(
        `/api/v1/discovery/families/${encodeURIComponent(entityId)}/variants?limit=100`
      );
      const variantsData = variantsRes.ok ? await variantsRes.json() : {};
      const variants = Array.isArray(variantsData.items) ? variantsData.items : [];
      richHtml = renderFamilyInspector(payload, variants);
    } else if (entityType === 'categories') {
      const treeRes = await fetch('/api/v1/discovery/categories/tree?with_position=true');
      const treeData = treeRes.ok ? await treeRes.json() : {};
      const tree = Array.isArray(treeData.tree) ? treeData.tree : [];
      richHtml = renderCategoryInspector(payload, tree);
    } else if (entityType === 'media_files') {
      richHtml = renderMediaFileInspector(payload);
    } else if (entityType === 'asset_families') {
      richHtml = renderAssetFamilyInspector(payload);
    } else if (entityType === 'reference_entities') {
      richHtml = renderReferenceEntityInspector(payload);
    } else if (entityType === 'reference_entity_records') {
      richHtml = renderReferenceRecordInspector(payload);
    } else if (entityType === 'measurement_families') {
      richHtml = renderMeasurementFamilyInspector(payload);
    } else if (entityType === 'users') {
      richHtml = renderUserInspector(payload);
    }

    if (codeEl) {
      if (richHtml) {
        codeEl.innerHTML = richHtml;
      } else {
        codeEl.innerHTML = `<code>${escapeHtml(JSON.stringify(payload, null, 2))}</code>`;
      }
    }

    // Inspect target prerequisites asynchronously
    fetchEntityDependencies(entityType, entityId).then((resolution) => {
      renderDependenciesCard(depsPanel, depsBadge, null, depsHint, resolution, entityType, entityId);
    });
  } catch (err: any) {
    if (codeEl) {
      const is403 =
        err.status === 403 ||
        err.data?.code === 'akeneo_permission_denied' ||
        err.data?.error === 'akeneo_permission_denied' ||
        String(err.message).includes('403');
      if (is403) {
        codeEl.innerHTML = `
          <div class="permission-warning-card alert alert-warning" style="padding: 1.25rem; border-radius: 8px;">
            <h4 style="margin: 0 0 0.5rem 0;">⚠️ Akeneo 403 Forbidden: Permission Denied</h4>
            <p style="margin: 0 0 0.5rem 0;">The Akeneo API user/role requires <strong>Product View</strong>, <strong>Category Tree access (Read/Write)</strong>, <strong>Locale access</strong>, and <strong>Event Platform permissions</strong>.</p>
            <p style="margin: 0; font-size: 0.85rem; color: var(--text-secondary);">Verify API user permissions in Akeneo PIM System Settings.</p>
          </div>
        `;
      } else {
        codeEl.innerHTML = `<code class="text-danger">Failed to fetch entity details: ${escapeHtml(err.message)}</code>`;
      }
    }
  }
}

export function toggleExplorerItemSelection(entityId: string, isSelected: boolean): void {
  if (isSelected) {
    state.explorer.selectedItemIds.add(entityId);
  } else {
    state.explorer.selectedItemIds.delete(entityId);
  }
  updateExplorerSelectionBar();
  renderExplorerTable();
}

export function selectAllExplorerItems(isSelected: boolean): void {
  const items = state.explorer.items;
  items.forEach((item) => {
    const id = getItemId(item);
    if (id) {
      if (isSelected) {
        state.explorer.selectedItemIds.add(id);
      } else {
        state.explorer.selectedItemIds.delete(id);
      }
    }
  });
  renderExplorerTable();
  updateExplorerSelectionBar();
}

export function clearExplorerSelection(): void {
  state.explorer.selectedItemIds.clear();
  renderExplorerTable();
  updateExplorerSelectionBar();
}

export function updateExplorerSelectionBar(): void {
  const bar = document.getElementById('explorer-selection-bar');
  const countEl = document.getElementById('explorer-selected-count');
  const count = state.explorer.selectedItemIds.size;

  if (countEl) countEl.textContent = count.toLocaleString();

  if (bar) {
    bar.style.display = count > 0 ? 'flex' : 'none';
  }
}

export function setExplorerTargetMode(mode: 'existing' | 'new'): void {
  const existingGroup = document.getElementById('explorer-target-existing-group');
  const newGroup = document.getElementById('explorer-target-new-group');
  const existingRadio = document.getElementById('explorer-target-mode-existing') as HTMLInputElement | null;
  const newRadio = document.getElementById('explorer-target-mode-new') as HTMLInputElement | null;

  if (existingRadio) existingRadio.checked = mode === 'existing';
  if (newRadio) newRadio.checked = mode === 'new';

  if (existingGroup) existingGroup.style.display = mode === 'existing' ? 'block' : 'none';
  if (newGroup) newGroup.style.display = mode === 'new' ? 'block' : 'none';
}

export async function openAddToVirtualTargetModal(
  entities?: Array<{ entity_type: string; entity_id: string }>
): Promise<void> {
  const modal = document.getElementById('explorer-add-to-target-modal');
  const countEl = document.getElementById('explorer-target-entity-count');
  const previewEl = document.getElementById('explorer-target-entity-preview');
  const targetSelect = document.getElementById('explorer-target-select') as HTMLSelectElement | null;
  const newNameInput = document.getElementById('explorer-new-target-name') as HTMLInputElement | null;
  const newDescInput = document.getElementById('explorer-new-target-description') as HTMLInputElement | null;
  const newScenarioSelect = document.getElementById('explorer-new-target-scenario') as HTMLSelectElement | null;
  const resolveDepsCb = document.getElementById('explorer-target-resolve-deps') as HTMLInputElement | null;
  const includeMediaCb = document.getElementById('explorer-target-include-media') as HTMLInputElement | null;

  if (!modal) return;

  let targetEntities: Array<{ entity_type: string; entity_id: string }>;

  if (entities && entities.length > 0) {
    targetEntities = entities;
  } else {
    const entityType = state.explorer.entityType;
    const selectedIds = Array.from(state.explorer.selectedItemIds);
    if (selectedIds.length === 0) {
      showToast('No items selected to add to virtual target', 'warning');
      return;
    }
    targetEntities = selectedIds.map((id) => ({
      entity_type: entityType,
      entity_id: id,
    }));
  }

  state.explorer.pendingTargetEntities = targetEntities;

  if (countEl) countEl.textContent = String(targetEntities.length);
  if (previewEl) {
    previewEl.innerHTML = `
      <ul style="margin: 0; padding-left: 1.25rem; line-height: 1.6;">
        ${targetEntities
          .map(
            (e) =>
              `<li><span class="badge badge-neutral" style="margin-right: 0.35rem;">${escapeHtml(e.entity_type)}</span><strong class="mono">${escapeHtml(e.entity_id)}</strong></li>`
          )
          .join('')}
      </ul>
    `;
  }

  // Reset inputs & options
  if (resolveDepsCb) resolveDepsCb.checked = false;
  if (includeMediaCb) includeMediaCb.checked = true;
  if (newNameInput) newNameInput.value = '';
  if (newDescInput) newDescInput.value = '';
  if (newScenarioSelect) newScenarioSelect.value = 'custom';

  // Load / fetch virtual target packages
  try {
    const res = await fetch('/api/v1/staging/packages');
    if (res.ok) {
      const data = await res.json();
      state.staging.packages = data.packages || [];
    }
  } catch {
    // Keep cached packages in state if fetch fails
  }

  const packages = state.staging.packages || [];

  if (targetSelect) {
    if (packages.length > 0) {
      targetSelect.innerHTML = packages
        .map(
          (pkg) =>
            `<option value="${escapeHtml(pkg.id)}">${escapeHtml(pkg.name)} (${escapeHtml(pkg.status)})</option>`
        )
        .join('');

      if (state.staging.selectedPackageId && packages.some((p) => p.id === state.staging.selectedPackageId)) {
        targetSelect.value = state.staging.selectedPackageId;
      } else {
        targetSelect.value = packages[0].id;
      }
      setExplorerTargetMode('existing');
    } else {
      targetSelect.innerHTML = '<option value="">No existing packages found</option>';
      setExplorerTargetMode('new');
    }
  } else {
    setExplorerTargetMode(packages.length > 0 ? 'existing' : 'new');
  }

  modal.style.display = 'flex';
}

export function closeAddToVirtualTargetModal(): void {
  const modal = document.getElementById('explorer-add-to-target-modal');
  if (modal) modal.style.display = 'none';
  state.explorer.pendingTargetEntities = undefined;
}

export async function confirmAddToVirtualTarget(): Promise<void> {
  const pending = state.explorer.pendingTargetEntities;
  if (!pending || pending.length === 0) {
    showToast('No entities to add', 'warning');
    closeAddToVirtualTargetModal();
    return;
  }

  const newRadio = document.getElementById('explorer-target-mode-new') as HTMLInputElement | null;
  const isNewMode = Boolean(newRadio && newRadio.checked);

  let targetPackageId: string;

  if (isNewMode) {
    const nameInput = document.getElementById('explorer-new-target-name') as HTMLInputElement | null;
    const descInput = document.getElementById('explorer-new-target-description') as HTMLInputElement | null;
    const scenarioSelect = document.getElementById('explorer-new-target-scenario') as HTMLSelectElement | null;

    const name = nameInput?.value?.trim() || '';
    if (!name) {
      showToast('Virtual Target Name is required', 'warning');
      return;
    }

    const description = descInput?.value?.trim() || undefined;
    const scenario = scenarioSelect?.value || 'custom';

    try {
      const createRes = await fetch('/api/v1/staging/packages', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, description, scenario }),
      });

      if (!createRes.ok) {
        const errData = await createRes.json().catch(() => ({}));
        throw new Error(errData.message || `HTTP ${createRes.status}`);
      }

      const createData = await createRes.json();
      targetPackageId = createData.package.id;
      if (createData.package) {
        state.staging.packages.unshift(createData.package);
      }
    } catch (err: any) {
      showToast(`Failed to create virtual target: ${err.message}`, 'error', 5000);
      return;
    }
  } else {
    const targetSelect = document.getElementById('explorer-target-select') as HTMLSelectElement | null;
    const selectedVal = targetSelect?.value;
    if (!selectedVal) {
      showToast('Please select a virtual target package', 'warning');
      return;
    }
    targetPackageId = selectedVal;
  }

  const resolveDepsCb = document.getElementById('explorer-target-resolve-deps') as HTMLInputElement | null;
  const includeMediaCb = document.getElementById('explorer-target-include-media') as HTMLInputElement | null;

  const resolveDependencies = resolveDepsCb ? resolveDepsCb.checked : true;
  const includeMedia = includeMediaCb ? includeMediaCb.checked : true;

  const confirmBtn = document.getElementById('btn-confirm-explorer-add-target') as HTMLButtonElement | null;
  if (confirmBtn) {
    confirmBtn.disabled = true;
    confirmBtn.innerHTML = '<span class="spinner-sm"></span> Adding...';
  }

  try {
    const res = await fetch(`/api/v1/staging/packages/${encodeURIComponent(targetPackageId)}/populate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        entities: pending,
        resolve_dependencies: resolveDependencies,
        include_media: includeMedia,
      }),
    });

    if (!res.ok) {
      const errData = await res.json().catch(() => ({}));
      throw new Error(errData.message || `HTTP ${res.status}`);
    }

    const data = await res.json();
    const count = data.populated_count ?? data.items_created ?? pending.length;
    showToast(`Successfully added ${count} entity(ies) to virtual target`, 'success', 4000);

    closeAddToVirtualTargetModal();
    clearExplorerSelection();
    await selectStagingPackage(targetPackageId);
  } catch (err: any) {
    showToast(`Add to virtual target failed: ${err.message}`, 'error', 5000);
  } finally {
    if (confirmBtn) {
      confirmBtn.disabled = false;
      confirmBtn.innerHTML = '<span>Add to Virtual Target</span>';
    }
  }
}

export async function deleteExplorerItem(entityId: string): Promise<void> {
  const entityType = state.explorer.entityType;
  const confirmed = typeof window !== 'undefined' && window.confirm
    ? window.confirm(`Are you sure you want to delete ${entityType} '${entityId}' from the target instance? This action cannot be undone.`)
    : true;

  if (!confirmed) return;

  try {
    const res = await fetch(`/api/v1/entities/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!res.ok) {
      const errData = await res.json().catch(() => ({}));
      throw new Error(errData.message || `HTTP ${res.status}`);
    }

    state.explorer.selectedItemIds.delete(entityId);
    showToast(`Deleted ${entityType} '${entityId}' from target`, 'success', 3000);
    fetchExplorerItems();
  } catch (err: any) {
    showToast(`Delete failed: ${err.message || String(err)}`, 'error', 5000);
  }
}

export function openBatchDeleteModal(): void {
  const modal = document.getElementById('explorer-batch-delete-modal');
  const totalEl = document.getElementById('explorer-batch-delete-total');
  const listEl = document.getElementById('explorer-batch-delete-list');
  const confirmCb = document.getElementById('explorer-batch-delete-confirm-cb') as HTMLInputElement | null;
  const confirmBtn = document.getElementById('btn-confirm-explorer-batch-delete') as HTMLButtonElement | null;

  if (!modal) return;

  const selectedIds = Array.from(state.explorer.selectedItemIds);
  const entityType = state.explorer.entityType;

  if (totalEl) totalEl.textContent = String(selectedIds.length);

  if (listEl) {
    const items = state.explorer.items.filter((item) => state.explorer.selectedItemIds.has(getItemId(item)));
    if (items.length === 0) {
      listEl.innerHTML = '<p class="text-secondary">Selected items are not visible on the current page.</p>';
    } else {
      listEl.innerHTML = `
        <ul style="margin: 0; padding-left: 1.25rem; line-height: 1.6;">
          ${items.map((item) => `<li class="mono">${escapeHtml(getItemId(item))}</li>`).join('')}
        </ul>
      `;
    }
  }

  if (confirmCb) confirmCb.checked = false;
  if (confirmBtn) confirmBtn.disabled = true;

  modal.style.display = 'flex';
}

export function closeBatchDeleteModal(): void {
  const modal = document.getElementById('explorer-batch-delete-modal');
  if (modal) modal.style.display = 'none';
}

export async function confirmBatchDelete(): Promise<void> {
  const confirmCb = document.getElementById('explorer-batch-delete-confirm-cb') as HTMLInputElement | null;
  if (!confirmCb || !confirmCb.checked) {
    showToast('Please confirm the deletion by checking the safety checkbox', 'warning');
    return;
  }

  const entityType = state.explorer.entityType;
  const selectedIds = Array.from(state.explorer.selectedItemIds);
  if (selectedIds.length === 0) {
    showToast('No items selected for deletion', 'warning');
    return;
  }

  const items = selectedIds.map((id) => `${entityType}:${id}`);

  try {
    const res = await fetch('/api/v1/entities/batch-delete', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items }),
    });

    if (!res.ok) {
      const errData = await res.json().catch(() => ({}));
      throw new Error(errData.message || `HTTP ${res.status}`);
    }

    const data = await res.json();
    const deletedCount = data.deleted_count || selectedIds.length;
    showToast(`Batch deletion completed: ${deletedCount} item(s) deleted`, 'success', 4000);
  } catch (err: any) {
    showToast(`Batch delete failed: ${err.message || String(err)}`, 'error', 5000);
  } finally {
    closeBatchDeleteModal();
    clearExplorerSelection();
    fetchExplorerItems();
  }
}

export function syncExplorerItem(entityId: string): void {
  executeEntityCascadeSync(state.explorer.entityType, entityId);
}

export function initExplorer(): void {
  attachExplorerCategoryTabs();
  renderExplorerEntitySelector();
  renderExplorerCategoryTabs();
  fetchExplorerItems();
}

const DIFF_IGNORED_KEYS = new Set(['_links']);

function isScalar(value: unknown): boolean {
  return value === null || typeof value !== 'object';
}

function valuesEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (typeof a !== typeof b) return false;
  if (isScalar(a) || isScalar(b)) return a === b;
  return JSON.stringify(a) === JSON.stringify(b);
}

export function computeEntityDiff(source: unknown, target: unknown, path = '', changes: EntityDiffChange[] = []): EntityDiffChange[] {
  if (source === null && target === null) return changes;
  if (source === undefined && target === undefined) return changes;

  if (source === null || source === undefined || target === null || target === undefined) {
    const type: EntityDiffChange['type'] =
      source === null || source === undefined ? 'removed' : target === null || target === undefined ? 'added' : 'modified';
    changes.push({ path: path || '(root)', type, oldValue: target, newValue: source });
    return changes;
  }

  if (Array.isArray(source) || Array.isArray(target)) {
    if (!valuesEqual(source, target)) {
      changes.push({ path: path || '(root)', type: 'modified', oldValue: target, newValue: source });
    }
    return changes;
  }

  if (typeof source !== 'object' || typeof target !== 'object') {
    if (!valuesEqual(source, target)) {
      changes.push({ path: path || '(root)', type: 'modified', oldValue: target, newValue: source });
    }
    return changes;
  }

  const srcObj = source as Record<string, unknown>;
  const tgtObj = target as Record<string, unknown>;
  const keys = new Set([
    ...Object.keys(srcObj).filter((k) => !DIFF_IGNORED_KEYS.has(k)),
    ...Object.keys(tgtObj).filter((k) => !DIFF_IGNORED_KEYS.has(k)),
  ]);

  for (const key of keys) {
    const childPath = path ? `${path}.${key}` : key;
    const hasSource = Object.prototype.hasOwnProperty.call(srcObj, key);
    const hasTarget = Object.prototype.hasOwnProperty.call(tgtObj, key);

    if (!hasSource) {
      changes.push({ path: childPath, type: 'removed', oldValue: tgtObj[key], newValue: undefined });
    } else if (!hasTarget) {
      changes.push({ path: childPath, type: 'added', oldValue: undefined, newValue: srcObj[key] });
    } else {
      computeEntityDiff(srcObj[key], tgtObj[key], childPath, changes);
    }
  }

  return changes;
}

export function formatDiffValue(value: unknown): string {
  if (value === undefined) return '—';
  if (value === null) return 'null';
  if (typeof value === 'string') return escapeHtml(value);
  return escapeHtml(JSON.stringify(value, null, 2));
}

export function getDiffTypeClass(type: EntityDiffChange['type']): string {
  switch (type) {
    case 'added':
      return 'diff-added';
    case 'removed':
      return 'diff-removed';
    case 'modified':
      return 'diff-modified';
    default:
      return 'diff-unchanged';
  }
}

export function getDiffTypeLabel(type: EntityDiffChange['type']): string {
  switch (type) {
    case 'added':
      return 'Added';
    case 'removed':
      return 'Removed';
    case 'modified':
      return 'Modified';
    default:
      return 'Unchanged';
  }
}

export function renderEntityDiffModal(): void {
  const modal = document.getElementById('entity-diff-modal');
  const title = document.getElementById('entity-diff-title');
  const subtitle = document.getElementById('entity-diff-subtitle');
  const statusBadges = document.getElementById('entity-diff-status-badges');
  const summary = document.getElementById('entity-diff-summary');
  const fieldDiffContainer = document.getElementById('entity-diff-field-list');
  const sourceJson = document.getElementById('entity-diff-source-json');
  const targetJson = document.getElementById('entity-diff-target-json');
  const syncBtn = document.getElementById('btn-entity-diff-sync-now') as HTMLButtonElement | null;
  const spinner = document.getElementById('entity-diff-loading');

  if (!modal) return;

  const diff = state.explorer.diffModal;
  if (!diff) {
    modal.style.display = 'none';
    return;
  }

  const meta = getExplorerTypeMeta(diff.entityType);
  const isProductLike = isProductLikeEntityType(diff.entityType);
  const commercialName = isProductLike
    ? resolveProductCommercialName(diff.source) || resolveProductCommercialName(diff.target)
    : '';

  if (title) title.textContent = `Source vs Target Diff: ${meta.label}`;
  if (subtitle) {
    subtitle.textContent = commercialName
      ? `${escapeHtml(commercialName)} • ${escapeHtml(diff.entityId)}`
      : `Entity identifier: ${escapeHtml(diff.entityId)}`;
  }

  if (statusBadges) {
    const hasSource = diff.source !== null && diff.source !== undefined;
    const hasTarget = diff.target !== null && diff.target !== undefined;
    const sourceBadge = hasSource
      ? '<span class="badge badge-success">Source Exists</span>'
      : '<span class="badge badge-neutral">Source Missing</span>';
    const targetBadge = hasTarget
      ? '<span class="badge badge-info">Target Exists</span>'
      : '<span class="badge badge-warning">Target Missing</span>';
    const changeBadge =
      diff.changes.length === 0
        ? '<span class="badge badge-success">Identical</span>'
        : '<span class="badge badge-accent">Differences Detected</span>';
    statusBadges.innerHTML = `${sourceBadge} ${targetBadge} ${changeBadge}`;
  }

  if (summary) {
    const added = diff.changes.filter((c) => c.type === 'added').length;
    const removed = diff.changes.filter((c) => c.type === 'removed').length;
    const modified = diff.changes.filter((c) => c.type === 'modified').length;
    summary.innerHTML = `
      <div class="diff-summary-item diff-added">
        <span class="diff-summary-num">${added}</span>
        <span class="diff-summary-label">Added</span>
      </div>
      <div class="diff-summary-item diff-removed">
        <span class="diff-summary-num">${removed}</span>
        <span class="diff-summary-label">Removed</span>
      </div>
      <div class="diff-summary-item diff-modified">
        <span class="diff-summary-num">${modified}</span>
        <span class="diff-summary-label">Modified</span>
      </div>
      <div class="diff-summary-item">
        <span class="diff-summary-num">${diff.changes.length}</span>
        <span class="diff-summary-label">Total Changes</span>
      </div>
    `;
  }

  if (spinner) spinner.style.display = diff.loading ? 'flex' : 'none';

  if (fieldDiffContainer) {
    if (diff.changes.length === 0) {
      fieldDiffContainer.innerHTML = `
        <div class="empty-table-state" style="padding: 1.5rem;">
          <div class="empty-icon">✅</div>
          <h4>No Field Differences</h4>
          <p>Source and target payloads are identical (ignoring instance-specific links).</p>
        </div>
      `;
    } else {
      const rows = diff.changes
        .map(
          (change) => `
            <div class="diff-field-row ${getDiffTypeClass(change.type)}">
              <div class="diff-field-path mono">${escapeHtml(change.path)}</div>
              <div class="diff-field-values">
                <div class="diff-field-value diff-source-value">
                  <span class="diff-field-label">Source</span>
                  <pre class="diff-value-pre"><code>${formatDiffValue(change.newValue)}</code></pre>
                </div>
                <div class="diff-field-value diff-target-value">
                  <span class="diff-field-label">Target</span>
                  <pre class="diff-value-pre"><code>${formatDiffValue(change.oldValue)}</code></pre>
                </div>
              </div>
              <div class="diff-field-status">
                <span class="diff-status-badge ${getDiffTypeClass(change.type)}">${getDiffTypeLabel(change.type)}</span>
              </div>
            </div>
          `
        )
        .join('');
      fieldDiffContainer.innerHTML = rows;
    }
  }

  if (sourceJson) {
    sourceJson.innerHTML = diff.source !== undefined && diff.source !== null
      ? `<code>${escapeHtml(JSON.stringify(diff.source, null, 2))}</code>`
      : '<code class="text-secondary">Source entity not found</code>';
  }

  if (targetJson) {
    targetJson.innerHTML = diff.target !== undefined && diff.target !== null
      ? `<code>${escapeHtml(JSON.stringify(diff.target, null, 2))}</code>`
      : '<code class="text-secondary">Target entity not found</code>';
  }

  if (syncBtn) {
    syncBtn.disabled = diff.loading || diff.syncing;
    syncBtn.innerHTML = diff.syncing
      ? '<span class="spinner-sm"></span> Syncing...'
      : '<span>⚡ Sync to Target Now</span>';
  }
}

export function closeEntityDiffModal(): void {
  const modal = document.getElementById('entity-diff-modal');
  if (modal) modal.style.display = 'none';
  state.explorer.diffModal = null;
}

export async function executeEntityDiffSync(): Promise<void> {
  const diff = state.explorer.diffModal;
  if (!diff) return;

  state.explorer.diffModal = { ...diff, syncing: true };
  renderEntityDiffModal();

  try {
    await executeBatchSyncJob(false, {
      items: [diff.entityId],
      entity_types: [diff.entityType],
    });
    showToast(`Entity ${diff.entityId} synced to target`, 'success', 3000);
  } catch (err: any) {
    showToast(`Sync failed: ${err.message || String(err)}`, 'error', 5000);
  } finally {
    if (state.explorer.diffModal) {
      state.explorer.diffModal = { ...state.explorer.diffModal, syncing: false };
      renderEntityDiffModal();
    }
  }
}

export async function compareExplorerEntity(entityId: string): Promise<void> {
  const modal = document.getElementById('entity-diff-modal');
  const entityType = state.explorer.entityType;
  const depsPanel = document.getElementById('entity-diff-deps-panel');
  const depsBadge = document.getElementById('entity-diff-deps-badge');
  const depsList = document.getElementById('entity-diff-deps-list');
  const depsHint = document.getElementById('entity-diff-deps-hint');

  if (depsPanel) {
    depsPanel.style.display = 'block';
    const loadingEl = document.getElementById('entity-diff-analysis-loading');
    const contentEl = document.getElementById('entity-diff-analysis-content');
    if (loadingEl) loadingEl.style.display = 'flex';
    if (contentEl) contentEl.style.display = 'none';

    const syncBtn = document.getElementById('btn-cascade-sync-diff');
    if (syncBtn) {
      syncBtn.setAttribute('disabled', 'true');
      syncBtn.innerHTML = '<span class="spinner-sm"></span> <span>Analyzing Prerequisites...</span>';
    }
  }

  state.explorer.diffModal = {
    entityType,
    entityId,
    source: null,
    target: null,
    changes: [],
    loading: true,
    error: null,
    syncing: false,
  };

  if (modal) modal.style.display = 'flex';
  renderEntityDiffModal();

  fetchEntityDependencies(entityType, entityId).then((resolution) => {
    renderDependenciesCard(depsPanel, depsBadge, depsList, depsHint, resolution, entityType, entityId, 'diff');
  });

  try {
    const [sourceRes, targetRes] = await Promise.all([
      fetch(`/api/v1/discovery/entities/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}?connection=source`),
      fetch(`/api/v1/discovery/entities/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}?connection=target`),
    ]);

    let sourcePayload: unknown = null;
    let targetPayload: unknown = null;

    if (sourceRes.ok) {
      const sourceData = await sourceRes.json();
      sourcePayload = sourceData.item ?? sourceData.product ?? sourceData.family ?? sourceData.category ?? sourceData.attribute ?? sourceData.channel ?? sourceData.locale ?? sourceData.currency ?? sourceData.asset_family ?? sourceData.media_file ?? sourceData;
    }
    if (targetRes.ok) {
      const targetData = await targetRes.json();
      targetPayload = targetData.item ?? targetData.product ?? targetData.family ?? targetData.category ?? targetData.attribute ?? targetData.channel ?? targetData.locale ?? targetData.currency ?? targetData.asset_family ?? targetData.media_file ?? targetData;
    }

    const changes = computeEntityDiff(sourcePayload, targetPayload);

    state.explorer.diffModal = {
      entityType,
      entityId,
      source: sourcePayload,
      target: targetPayload,
      changes,
      loading: false,
      error: null,
      syncing: false,
    };
  } catch (err: any) {
    state.explorer.diffModal = {
      entityType,
      entityId,
      source: null,
      target: null,
      changes: [],
      loading: false,
      error: err.message || String(err),
      syncing: false,
    };
    showToast(`Diff comparison failed: ${err.message || String(err)}`, 'error', 5000);
  }

  renderEntityDiffModal();
}


